import { BACKEND_URL } from '../config/backendUrl';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PdfEditorToolbar } from '../components/template-editor/PdfEditorToolbar';
import { templatesApi, bullhornApi } from '../services/api';
import {
  findInvalidConditionReferences,
  reconcileFieldConditions,
  removeFieldAndConditionReferences,
} from '../utils/fieldConditions';
import { parseCcEmails } from '../utils/parseCcEmails';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Trash2, 
  Save,
  RefreshCw,
  Type,
  Calendar,
  CheckSquare,
  ChevronDown,
  PenTool,
  GripVertical,
  FileText,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  Upload,
  Mail,
  Bell,
  Timer,
  Phone,
  Hash,
  Move,
  Lock,
  Unlock,
  Eye,
  ArrowUpDown,
  Database,
  User,
  Building2,
  Magnet,
  Undo2,
  Redo2,
  Star,
  Search,
  Circle,
  GitBranch,
  ShieldCheck,
  Beaker,
  CheckCircle2,
  XCircle,
  Pencil,
  Sparkles,
  Users,
  Settings as SettingsIcon,
  Plus,
  Minus,
  Paperclip,
  AlignLeft,
  AlignCenter,
  AlignRight,
  ArrowUpToLine,
  AlignCenterVertical,
  ArrowDownToLine,
  X as XIcon,
  MapPin
} from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
// Stable ref so react-pdf sends the auth cookie on cross-origin (Pages→Worker)
// PDF fetches, without reloading on every render.
const PDF_HTTP_OPTIONS = { withCredentials: true };
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { RichTextEditor } from '../components/RichTextEditor';
import axios from 'axios';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const API_URL = `${BACKEND_URL}/api`;

/**
 * Search input designed to live INSIDE a Radix `<Select>` dropdown.
 * Radix's SelectContent attaches a `keydown` listener that implements
 * typeahead — it calls `item.ref.current.focus()` via `setTimeout(...,0)`
 * after every printable keystroke, which steals focus from any embedded
 * input and caused "Cannot read properties of null (reading 'focus')"
 * crashes and blocked typing past the first character.
 *
 * Neither React synthetic `stopPropagation` nor native `stopPropagation`
 * reliably blocks Radix's listener because the event traverses Radix's
 * Portal tree through React's delegated event system.
 *
 * The pragmatic fix: let the keystroke through (so the `input` event
 * still updates our state), then re-focus the input on the next tick —
 * after Radix's `setTimeout(0)` has moved focus to a SelectItem. The
 * user never sees the focus flicker because the DOM updates in the same
 * frame.
 */
const SelectSearchInput = ({ value, onChange, placeholder, testId, className }) => {
  const inputRef = useRef(null);

  return (
    <input
      ref={inputRef}
      type="text"
      placeholder={placeholder}
      className={className}
      value={value}
      onChange={(e) => {
        onChange(e);
        // Radix's typeahead uses setTimeout(0) to focus the matching item.
        // Schedule our refocus AFTER it has run so focus returns to the input.
        const el = e.currentTarget;
        setTimeout(() => {
          if (el && document.activeElement !== el) {
            el.focus();
            // Move cursor to end so the user can keep typing naturally
            const len = el.value.length;
            try { el.setSelectionRange(len, len); } catch (err) {
              // setSelectionRange throws on inputs that don't support
              // selection (e.g. type="number"). Cursor-position is a
              // nice-to-have, so swallow the error after logging once.
              console.debug('setSelectionRange skipped:', err?.message);
            }
          }
        }, 0);
      }}
      onKeyDown={(e) => e.stopPropagation()}
      onKeyUp={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      data-testid={testId}
    />
  );
};


// Snap threshold in pixels
const SNAP_THRESHOLD = 8;
const GRID_SIZES = [1, 2, 5, 10, 20];
const DEFAULT_GRID_SIZE = 5;
const KEYBOARD_MOVE_SMALL = 1; // Arrow keys
const KEYBOARD_MOVE_LARGE = 10; // Shift + Arrow keys

// Font size override — the starting point pdfTextLayout uses on the final
// PDF (it still shrinks further on its own if the value doesn't fit the
// box, so these bounds are about a sane manual range, not a hard render
// limit). DEFAULT matches pdfTextLayout's own unset-override starting size
// for an ordinary single-line field, so the first +/- click nudges from
// what the field already looks like rather than jumping to an arbitrary value.
const FONT_SIZE_MIN = 6;
const FONT_SIZE_MAX = 24;
const FONT_SIZE_DEFAULT = 10;

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input', icon: Type },
  { value: 'date', label: 'Date', icon: Calendar },
  { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  { value: 'radio_group', label: 'Radio Group', icon: Circle },
  { value: 'dropdown', label: 'Dropdown', icon: ChevronDown },
  { value: 'signature', label: 'Signature', icon: PenTool },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'phone', label: 'Phone', icon: Phone },
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'file_upload', label: 'File Upload', icon: Upload },
];

// Which Bullhorn field data types a HyreDocs field type can be mapped to.
// Each predicate takes a Bullhorn field option ({ dataType, is_picklist }).
// `is_picklist` is true when Bullhorn reports inline options, an optionsType,
// or an optionsUrl (entity-backed picklists like Country / Skill).
// `signature` and `file_upload` can't be mapped at all. Options with an
// unknown dataType are left unfiltered.
const isString = (opt) => ['string', 'text'].includes(String(opt?.dataType || '').toLowerCase());
// Free-text targets: a String/Text that is NOT a picklist.
const isPlainTextBullhornField = (opt) => isString(opt) && !opt?.is_picklist;
// Dropdown / radio have their own choice list — they can map to any String/Text
// field (picklist or plain), or to an Integer *picklist* (e.g. address.countryID,
// whose options are country IDs).
const isChoiceMappableBullhornField = (opt) =>
  isString(opt) || (String(opt?.dataType || '').toLowerCase() === 'integer' && !!opt?.is_picklist);
const FIELD_TYPE_BULLHORN_COMPAT = {
  // A Text Input can also hold a whole composite (address) as a one-line,
  // read-only string.
  text: (opt) => isPlainTextBullhornField(opt) || !!opt?.is_composite,
  email: isPlainTextBullhornField,
  phone: isPlainTextBullhornField,
  radio_group: isChoiceMappableBullhornField,
  dropdown: isChoiceMappableBullhornField,
  date: (opt) => ['timestamp', 'datetime'].includes(String(opt?.dataType || '').toLowerCase()),
  checkbox: (opt) => String(opt?.dataType || '').toLowerCase() === 'boolean',
  number: (opt) => {
    const dt = String(opt?.dataType || '').toLowerCase();
    return dt === 'integer' || dt === 'double' || dt === 'bigdecimal' || isPlainTextBullhornField(opt);
  },
  signature: () => false,
  file_upload: () => false,
};

// The whole composite object — e.g. `address`, `candidate.address`,
// `company.address`. Bullhorn only accepts the address as a whole, so a field
// mapped to this is kept read-only; its individual parts (`address.city`, …)
// are normal editable mappings. Backend flags the parent `is_composite`; the
// regex covers the hardcoded fallback lists.
const bullhornFieldIsComposite = (opt) =>
  !!opt?.is_composite || /(?:^|\.)[a-z]*address$/i.test(String(opt?.value || opt?.name || ''));

// Infer a currency / unit symbol from a number field's label. Used to
// auto-populate ``currency_symbol`` on field creation and on label edit
// (only when the recruiter hasn't already set one). Returns ``null``
// when no keyword matches — keeping the field a plain number.
//
// Order matters: "%" check first so "percentage rate" routes to "%"
// rather than the tenant's money symbol.
const inferCurrencyFromLabel = (label, tenantDefaultSymbol = '£') => {
  if (!label) return null;
  const l = String(label).toLowerCase();
  if (/(percentage|percent|%|tax rate|vat rate)/.test(l)) {
    return { symbol: '%', position: 'suffix', formatMoney: false };
  }
  if (/(rate|wage|salary|fee|amount|cost|price|stipend|allowance|bonus|commission|day rate|hourly|pay\b|charge)/.test(l)) {
    return { symbol: tenantDefaultSymbol || '£', position: 'prefix', formatMoney: true };
  }
  return null;
};


// Frequently used fields (shown at top of dropdown)
const FREQUENTLY_USED_FIELDS = [
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'mobile', label: 'Mobile' },
  { value: 'address.address1', label: 'Address Line 1' },
  { value: 'address.address2', label: 'Address Line 2' },
  { value: 'address.city', label: 'City' },
  { value: 'address.state', label: 'County / State / Province' },
  { value: 'address.zip', label: 'Post Code / ZIP Code' },
  { value: 'address.countryID', label: 'Country' },
  { value: 'dateOfBirth', label: 'Date of Birth' },
  { value: 'ssn', label: 'SSN / NI Number' },
];

// Default Bullhorn field mappings (fallback when API unavailable). `dataType` /
// `is_picklist` mirror what Bullhorn's meta reports, so the map-to control can
// filter these the same way it filters live schema fields.
const BULLHORN_CANDIDATE_FIELDS = [
  { value: 'none', label: 'No mapping' },
  { value: 'firstName', label: 'First Name', dataType: 'String' },
  { value: 'lastName', label: 'Last Name', dataType: 'String' },
  { value: 'email', label: 'Email', dataType: 'String' },
  { value: 'phone', label: 'Phone', dataType: 'String' },
  { value: 'mobile', label: 'Mobile', dataType: 'String' },
  { value: 'address', label: 'Address (whole)', dataType: 'Object', is_composite: true, composite_parts: ['Address Line 1', 'Address Line 2', 'City', 'County / State / Province', 'Post Code / ZIP Code', 'Country'] },
  { value: 'address.address1', label: 'Address Line 1', dataType: 'String' },
  { value: 'address.address2', label: 'Address Line 2', dataType: 'String' },
  { value: 'address.city', label: 'City', dataType: 'String' },
  { value: 'address.state', label: 'County / State / Province', dataType: 'String' },
  { value: 'address.zip', label: 'Post Code / ZIP Code', dataType: 'String' },
  { value: 'address.countryID', label: 'Country', dataType: 'Integer', is_picklist: true },
  { value: 'ssn', label: 'SSN / NI Number', dataType: 'String' },
  { value: 'dateOfBirth', label: 'Date of Birth', dataType: 'Timestamp' },
  { value: 'salary', label: 'Salary', dataType: 'Double' },
  { value: 'payRate', label: 'Pay Rate', dataType: 'Double' },
  { value: 'employeeType', label: 'Employee Type', dataType: 'String', is_picklist: true },
  { value: 'status', label: 'Status', dataType: 'String', is_picklist: true },
  { value: 'customText1', label: 'Custom Text 1', dataType: 'String' },
  { value: 'customText2', label: 'Custom Text 2', dataType: 'String' },
  { value: 'customText3', label: 'Custom Text 3', dataType: 'String' },
];

const BULLHORN_CONTACT_FIELDS = [
  { value: 'none', label: 'No mapping' },
  { value: 'firstName', label: 'First Name', dataType: 'String' },
  { value: 'lastName', label: 'Last Name', dataType: 'String' },
  { value: 'email', label: 'Email', dataType: 'String' },
  { value: 'phone', label: 'Phone', dataType: 'String' },
  { value: 'mobile', label: 'Mobile', dataType: 'String' },
  { value: 'title', label: 'Job Title', dataType: 'String' },
  { value: 'address', label: 'Address (whole)', dataType: 'Object', is_composite: true, composite_parts: ['Address Line 1', 'Address Line 2', 'City', 'County / State / Province', 'Post Code / ZIP Code', 'Country'] },
  { value: 'address.address1', label: 'Address Line 1', dataType: 'String' },
  { value: 'address.address2', label: 'Address Line 2', dataType: 'String' },
  { value: 'address.city', label: 'City', dataType: 'String' },
  { value: 'address.state', label: 'County / State / Province', dataType: 'String' },
  { value: 'address.zip', label: 'Post Code / ZIP Code', dataType: 'String' },
  { value: 'address.countryID', label: 'Country', dataType: 'Integer', is_picklist: true },
  { value: 'customText1', label: 'Custom Text 1', dataType: 'String' },
  { value: 'customText2', label: 'Custom Text 2', dataType: 'String' },
  { value: 'customText3', label: 'Custom Text 3', dataType: 'String' },
];

const BULLHORN_COMPANY_FIELDS = [
  { value: 'none', label: 'No mapping' },
  { value: 'company.name', label: 'Company Name', dataType: 'String' },
  { value: 'company.phone', label: 'Company Phone', dataType: 'String' },
  { value: 'company.address', label: 'Company Address (whole)', dataType: 'Object', is_composite: true, composite_parts: ['Company Address Line 1', 'Company Address Line 2', 'Company City', 'Company State', 'Company Zip Code'] },
  { value: 'company.address.address1', label: 'Company Address Line 1', dataType: 'String' },
  { value: 'company.address.address2', label: 'Company Address Line 2', dataType: 'String' },
  { value: 'company.address.city', label: 'Company City', dataType: 'String' },
  { value: 'company.address.state', label: 'Company State', dataType: 'String' },
  { value: 'company.address.zip', label: 'Company Zip Code', dataType: 'String' },
  { value: 'company.customText1', label: 'Company Custom Text 1', dataType: 'String' },
  { value: 'company.customText2', label: 'Company Custom Text 2', dataType: 'String' },
  { value: 'company.customText3', label: 'Company Custom Text 3', dataType: 'String' },
];

// Default Placement Bullhorn fields
const BULLHORN_PLACEMENT_FIELDS = [
  { value: 'none', label: 'No mapping' },
  // Placement Core Fields
  { value: 'status', label: 'Placement Status', dataType: 'String', is_picklist: true },
  { value: 'dateBegin', label: 'Start Date', dataType: 'Timestamp' },
  { value: 'dateEnd', label: 'End Date', dataType: 'Timestamp' },
  { value: 'employmentType', label: 'Employment Type', dataType: 'String', is_picklist: true },
  { value: 'salary', label: 'Salary', dataType: 'Double' },
  { value: 'payRate', label: 'Pay Rate', dataType: 'Double' },
  { value: 'clientBillRate', label: 'Bill Rate', dataType: 'Double' },
  { value: 'comments', label: 'Comments', dataType: 'String' },
  { value: 'location', label: 'Location', dataType: 'Object', is_composite: true },
  // Nested Candidate Fields
  { value: 'candidate.firstName', label: 'Candidate First Name', dataType: 'String' },
  { value: 'candidate.lastName', label: 'Candidate Last Name', dataType: 'String' },
  { value: 'candidate.email', label: 'Candidate Email', dataType: 'String' },
  { value: 'candidate.phone', label: 'Candidate Phone', dataType: 'String' },
  { value: 'candidate.mobile', label: 'Candidate Mobile', dataType: 'String' },
  { value: 'candidate.address', label: 'Candidate Address (whole)', dataType: 'Object', is_composite: true, composite_parts: ['Candidate Address', 'Candidate Address Line 2', 'Candidate City', 'Candidate County / State / Province', 'Candidate Post Code / ZIP Code', 'Candidate Country'] },
  { value: 'candidate.address.address1', label: 'Candidate Address', dataType: 'String' },
  { value: 'candidate.address.address2', label: 'Candidate Address Line 2', dataType: 'String' },
  { value: 'candidate.address.city', label: 'Candidate City', dataType: 'String' },
  { value: 'candidate.address.state', label: 'Candidate County / State / Province', dataType: 'String' },
  { value: 'candidate.address.zip', label: 'Candidate Post Code / ZIP Code', dataType: 'String' },
  { value: 'candidate.address.countryID', label: 'Candidate Country', dataType: 'Integer', is_picklist: true },
  { value: 'candidate.dateOfBirth', label: 'Candidate DOB', dataType: 'Timestamp' },
  { value: 'candidate.ssn', label: 'Candidate SSN/NI', dataType: 'String' },
  // Nested JobOrder Fields
  { value: 'jobOrder.title', label: 'Job Title', dataType: 'String' },
  { value: 'jobOrder.employmentType', label: 'Job Employment Type', dataType: 'String', is_picklist: true },
  { value: 'jobOrder.address.city', label: 'Job Location City', dataType: 'String' },
  // Nested Company Fields
  { value: 'clientCorporation.name', label: 'Company Name', dataType: 'String' },
  { value: 'clientCorporation.address.city', label: 'Company City', dataType: 'String' },
];

// Bullhorn entity colors (from Bullhorn official brand guidelines)
const ENTITY_COLORS = {
  candidate: { bg: 'bg-[#4CAF50]', text: 'text-white', label: 'Candidate', border: 'border-[#4CAF50]' },  // Green
  contact: { bg: 'bg-[#F9A825]', text: 'text-white', label: 'Contact', border: 'border-[#F9A825]' },  // Yellow/Orange
  placement: { bg: 'bg-[#1a4a5e]', text: 'text-white', label: 'Placement', border: 'border-[#1a4a5e]' },  // Deep Teal/Navy (Bullhorn)
};

const FIELD_COLORS = {
  text: 'bg-blue-50/80 border-blue-300',
  email: 'bg-cyan-50/80 border-cyan-300',
  phone: 'bg-teal-50/80 border-teal-300',
  number: 'bg-indigo-50/80 border-indigo-300',
  date: 'bg-purple-50/80 border-purple-300',
  checkbox: 'bg-green-50/80 border-green-300',
  radio_group: 'bg-lime-50/80 border-lime-300',
  dropdown: 'bg-orange-50/80 border-orange-300',
  signature: 'bg-pink-50/80 border-pink-300',
};


/**
 * Compute the rendered position of each radio_group option within its
 * field's bounding box. Three layouts:
 *   • vertical (default) — stacked, one per row
 *   • horizontal — spread evenly across width
 *   • custom — each option's offset is stored on `field.option_positions`
 *     so the recruiter can drag individual options to specific spots
 *     (e.g. over each radio circle on a Starter Checklist PDF).
 *
 * Returns design-unit coordinates (NOT scaled). Callers multiply by `scale`
 * for rendering and store the same units back on the field.
 */
export function computeRadioOptionLayout(field) {
  const options = field.options || [];
  const layout = field.options_layout || 'vertical';
  const positions = field.option_positions || {};
  const w = field.width || 0;
  const h = field.height || 0;
  const dotSize = 12;
  const inset = 4;

  return options.map((opt, idx) => {
    const label = typeof opt === 'string' ? opt : (opt?.label ?? String(opt));
    let x;
    let y;
    if (layout === 'custom' && positions[label]) {
      x = positions[label].x;
      y = positions[label].y;
    } else if (layout === 'horizontal' && options.length > 0) {
      const slot = w / options.length;
      x = slot * idx + inset;
      y = Math.max(inset, h / 2 - dotSize / 2);
    } else {
      // Vertical (default)
      const slot = options.length > 0 ? h / options.length : h;
      x = inset;
      y = slot * idx + Math.max(0, (slot - dotSize) / 2);
    }
    return { label, x, y };
  });
}


// ─────────────────────────────────────────────────────────────────────────────
// Bullhorn Credentials mapping card. Visible on candidate + placement templates.
// Lets the recruiter wire this template to one Bullhorn Certification (from the
// tenant's catalog) and tag one date field as the credential's expiry source.
// ─────────────────────────────────────────────────────────────────────────────
const CredentialMappingSection = ({
  enabled,
  setEnabled,
  credentialId,
  credentialName,
  onChange,
  fields,
  onToggleExpiryField,
  entityType,
}) => {
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');

  // Lazily fetch the catalog when the section is first enabled (or the
  // user opens it to change selection).
  const ensureLoaded = useCallback(async () => {
    if (catalog || loading) return;
    setLoading(true);
    setError(null);
    try {
      const res = await bullhornApi.getCredentials();
      if (res.data?.source === 'bullhorn') {
        setCatalog(res.data.credentials || []);
      } else {
        setCatalog([]);
        setError(
          res.data?.source === 'not_connected'
            ? 'Connect your Bullhorn account to load the credential catalog.'
            : 'Could not load credentials from Bullhorn.'
        );
      }
    } catch (e) {
      setError('Failed to load credentials.');
      setCatalog([]);
    } finally {
      setLoading(false);
    }
  }, [catalog, loading]);

  useEffect(() => {
    if (enabled) ensureLoaded();
  }, [enabled, ensureLoaded]);

  const filteredCatalog = useMemo(() => {
    const list = catalog || [];
    const q = search.trim().toLowerCase();
    if (!q) return list.slice(0, 60);
    return list.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 60);
  }, [catalog, search]);

  // Date fields that the recruiter can flag as the expiry source.
  const dateFields = (fields || []).filter((f) => f.field_type === 'date');
  const expiryFieldId = (fields || []).find((f) => f.is_credential_expiry)?.id || '';

  const selectedCatalogItem = (catalog || []).find((c) => Number(c.id) === Number(credentialId));

  return (
    <div className="space-y-3 border-t pt-5" data-testid="credential-mapping-section">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <Label className="flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-violet-600" />
            Bullhorn Credential
          </Label>
          <p className="text-xs text-muted-foreground mt-1">
            Write completed documents back to Bullhorn's Credentials tab.
            We'll create a new {entityType === 'placement' ? 'Placement Requirement' : 'Candidate Credential'} record
            with the expiry date you pick below — and attach the signed PDF to the credential itself.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(v) => {
            setEnabled(v);
            if (!v) onChange(null, '');
          }}
          data-testid="credential-mapping-toggle"
        />
      </div>

      {enabled && (
        <div className="space-y-3 rounded-lg border border-violet-200 bg-violet-50/40 p-3">
          {/* Credential picker */}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-violet-800">Which credential?</Label>
            {credentialId && selectedCatalogItem ? (
              <div className="flex items-center justify-between rounded-md bg-white border border-violet-200 px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <ShieldCheck className="w-4 h-4 text-violet-600 flex-shrink-0" />
                  <span className="text-sm font-medium truncate" data-testid="selected-credential-name">
                    {selectedCatalogItem.name}
                  </span>
                  <span className="text-[10px] text-violet-500 font-mono flex-shrink-0">#{selectedCatalogItem.id}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onChange(null, '')}
                  className="h-7 text-xs text-slate-500 hover:text-red-600"
                  data-testid="clear-selected-credential"
                >
                  Change
                </Button>
              </div>
            ) : credentialId ? (
              // Stored id but catalog not loaded / item missing — show the cached name.
              <div className="flex items-center justify-between rounded-md bg-white border border-violet-200 px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                  <ShieldCheck className="w-4 h-4 text-violet-600 flex-shrink-0" />
                  <span className="text-sm font-medium truncate">{credentialName || `Credential #${credentialId}`}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => onChange(null, '')}
                  className="h-7 text-xs text-slate-500 hover:text-red-600"
                >
                  Change
                </Button>
              </div>
            ) : (
              <>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={loading ? 'Loading catalog…' : `Search ${(catalog || []).length} credentials…`}
                  className="h-8 text-sm"
                  data-testid="credential-search-input"
                />
                {loading && <p className="text-xs text-slate-500">Loading…</p>}
                {error && <p className="text-xs text-red-600">{error}</p>}
                {!loading && !error && (
                  <div className="max-h-56 overflow-y-auto rounded-md border border-violet-200 bg-white divide-y divide-slate-100" data-testid="credential-options-list">
                    {filteredCatalog.length === 0 ? (
                      <p className="text-xs text-slate-500 px-3 py-3 text-center">No matching credentials.</p>
                    ) : (
                      filteredCatalog.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => onChange(c.id, c.name)}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-violet-50 transition-colors"
                          data-testid={`credential-option-${c.id}`}
                        >
                          <span className="text-sm truncate">{c.name}</span>
                          <span className="text-[10px] text-slate-400 font-mono flex-shrink-0">#{c.id}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Expiry field picker */}
          {credentialId && (
            <div className="space-y-1.5" data-testid="credential-expiry-section">
              <Label className="text-xs uppercase tracking-wider text-violet-800">Expiry date source</Label>
              <p className="text-[11px] text-muted-foreground">
                Pick a date field on this template. Whatever the candidate enters there becomes the credential's <code className="font-mono bg-white px-1 rounded">dateExpiration</code> in Bullhorn.
              </p>
              {dateFields.length === 0 ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Add a <strong>date</strong> field to this template, then come back here to tag it as the expiry.
                </p>
              ) : (
                <select
                  value={expiryFieldId || ''}
                  onChange={(e) => {
                    const id = e.target.value;
                    // Reset all flags then set the chosen one (single-select).
                    (fields || []).forEach((f) => {
                      if (f.is_credential_expiry && f.id !== id) onToggleExpiryField(f.id, false);
                    });
                    if (id) onToggleExpiryField(id, true);
                  }}
                  className="w-full h-8 text-sm rounded-md border border-violet-200 bg-white px-2"
                  data-testid="credential-expiry-field-select"
                >
                  <option value="">— No expiry date —</option>
                  {dateFields.map((f) => (
                    <option key={f.id} value={f.id}>{f.label || 'Date field'}</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};




// Draggable Field Component with Resize Handles
const DraggableField = ({ field, isSelected, scale, onSelect, onUpdate, onDelete, onSnapUpdate, onDragEnd }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialSize, setInitialSize] = useState({ width: 0, height: 0 });
  // When dragging a single radio-option (custom layout), this holds its label.
  const [draggingOption, setDraggingOption] = useState(null);
  const fieldRef = useRef(null);

  // Handle drag start
  const handleMouseDown = (e) => {
    if (e.target.dataset.resize) return; // Don't drag if clicking resize handle

    // Radio-group option drag (custom layout) — initiate per-option drag
    // and skip the whole-field drag handler so the user can place an
    // individual radio dot anywhere within the bounding box.
    const optEl = e.target.closest('[data-option-drag="1"]');
    if (optEl) {
      e.preventDefault();
      e.stopPropagation();
      const label = optEl.dataset.optionLabel;
      const cur = (field.option_positions || {})[label] || { x: 0, y: 0 };
      setDraggingOption(label);
      setDragStart({
        x: e.clientX - cur.x * scale,
        y: e.clientY - cur.y * scale,
      });
      onSelect(field.id);
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
    setDragStart({
      x: e.clientX - field.position_x * scale,
      y: e.clientY - field.position_y * scale,
    });
    onSelect(field.id);
  };

  // Handle resize start
  const handleResizeStart = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
    setDragStart({ x: e.clientX, y: e.clientY });
    setInitialSize({ width: field.width, height: field.height });
    onSelect(field.id);
  };

  const handleMouseMove = useCallback((e) => {
    if (draggingOption) {
      // Per-option positioning. Clamp inside bounding box so the option
      // stays visible. Stored in field design units, not scaled.
      const rawX = (e.clientX - dragStart.x) / scale;
      const rawY = (e.clientY - dragStart.y) / scale;
      let x = Math.max(0, Math.min(field.width - 10, rawX));
      let y = Math.max(0, Math.min(field.height - 10, rawY));

      // Snap-to-grid + snap-to-sibling-option alignment within the
      // field box. Mirrors the main-field snap behaviour so recruiters
      // can line up radio circles cleanly. The grid size is configurable
      // per field (`option_grid_size`, default 2). Holding Shift bypasses
      // the grid entirely for fine pixel-level placement.
      const shiftHeld = e.shiftKey;
      const SNAP_PX = 5 / scale;  // 5px on-screen sibling-align threshold
      const GRID = Math.max(1, parseInt(field.option_grid_size, 10) || 5);
      const siblings = Object.entries(field.option_positions || {})
        .filter(([label]) => label !== draggingOption)
        .map(([, pos]) => pos);
      // X-axis snap
      let snapped = false;
      for (const s of siblings) {
        if (Math.abs(x - s.x) < SNAP_PX) { x = s.x; snapped = true; break; }
      }
      if (!snapped && !shiftHeld) x = Math.round(x / GRID) * GRID;
      // Y-axis snap
      snapped = false;
      for (const s of siblings) {
        if (Math.abs(y - s.y) < SNAP_PX) { y = s.y; snapped = true; break; }
      }
      if (!snapped && !shiftHeld) y = Math.round(y / GRID) * GRID;

      const positions = { ...(field.option_positions || {}) };
      positions[draggingOption] = { x, y };
      onUpdate(field.id, { option_positions: positions });
      return;
    }
    if (isDragging) {
      const newX = Math.max(0, (e.clientX - dragStart.x) / scale);
      const newY = Math.max(0, (e.clientY - dragStart.y) / scale);
      // Use snap update if available
      if (onSnapUpdate) {
        onSnapUpdate(field.id, { position_x: newX, position_y: newY });
      } else {
        onUpdate(field.id, { position_x: newX, position_y: newY });
      }
    } else if (isResizing) {
      const deltaX = (e.clientX - dragStart.x) / scale;
      const deltaY = (e.clientY - dragStart.y) / scale;
      
      let newWidth = initialSize.width;
      let newHeight = initialSize.height;
      
      if (resizeDirection.includes('e')) newWidth = Math.max(30, initialSize.width + deltaX);
      if (resizeDirection.includes('w')) newWidth = Math.max(30, initialSize.width - deltaX);
      if (resizeDirection.includes('s')) newHeight = Math.max(15, initialSize.height + deltaY);
      if (resizeDirection.includes('n')) newHeight = Math.max(15, initialSize.height - deltaY);

      // For W/N handles we also need to shift position_x/position_y so the
      // OPPOSITE edge stays anchored as the user drags the near edge.
      let newX = field.position_x;
      let newY = field.position_y;
      if (resizeDirection.includes('w')) newX = field.position_x + (initialSize.width - newWidth);
      if (resizeDirection.includes('n')) newY = field.position_y + (initialSize.height - newHeight);

      if (onSnapUpdate) {
        onSnapUpdate(field.id, {
          width: newWidth,
          height: newHeight,
          position_x: newX,
          position_y: newY,
          _resizeDirection: resizeDirection,
        });
      } else {
        onUpdate(field.id, { width: newWidth, height: newHeight, position_x: newX, position_y: newY });
      }
    }
  }, [draggingOption, isDragging, isResizing, dragStart, scale, field.id, field.width, field.height, field.position_x, field.position_y, field.option_positions, onUpdate, onSnapUpdate, initialSize, resizeDirection]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
    setDraggingOption(null);
    // Clear snap guides when done dragging
    if (onDragEnd) onDragEnd();
  }, [onDragEnd]);

  useEffect(() => {
    if (isDragging || isResizing || draggingOption) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, draggingOption, handleMouseMove, handleMouseUp]);

  const scaledWidth = field.width * scale;
  const scaledHeight = field.height * scale;

  return (
    <div
      ref={fieldRef}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(field.id);
      }}
      onMouseDown={handleMouseDown}
      className={`absolute rounded-sm cursor-move select-none transition-shadow duration-100 ${
        isSelected
          ? 'ring-2 ring-[#f97316] ring-offset-1 shadow-lg z-20 border-2 border-[#f97316]'
          : 'hover:shadow z-10 border-2 hover:border-[#f97316]/50'
      } ${isDragging ? 'cursor-grabbing opacity-80' : ''} ${
        field.transparent_background && !isSelected
          ? 'bg-transparent border-dashed border-gray-300/70'
          : (FIELD_COLORS[field.field_type] || 'bg-gray-50 border-gray-300')
      }`}
      style={{
        left: field.position_x * scale,
        top: field.position_y * scale,
        width: scaledWidth,
        height: scaledHeight,
      }}
      data-testid={`field-${field.id}`}
      title={`${field.label}${field.required ? ' (Required)' : ''}\nDrag to move • Click corners to resize`}
    >
      {/* Field label / preview */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {field.field_type === 'radio_group' && (field.options || []).length > 0 ? (
          // Render each radio option at its computed position so the
          // recruiter sees exactly where it'll land on the PDF. In custom
          // layout mode the dots are pointer-events-auto and draggable.
          <>
            {computeRadioOptionLayout(field).map(({ label, x, y }, optIdx) => {
              const isDefault = String(field.default_value ?? '') === String(label);
              const isCustom = (field.options_layout || 'vertical') === 'custom';
              const dotSize = Math.max(8, Math.min(12, scaledHeight / 5));
              return (
                <div
                  // Keying by index keeps React happy even if two options
                  // share the same label (user typo, mid-edit blank, etc.) —
                  // a label-based key here was the reported "crash on add"
                  // symptom after a duplicate slipped through.
                  key={`${field.id}-opt-preview-${optIdx}`}
                  className={`absolute flex items-center gap-1 ${isCustom && isSelected ? 'pointer-events-auto cursor-grab' : ''}`}
                  style={{
                    left: x * scale,
                    top: y * scale,
                    fontSize: Math.max(8, Math.min(11, scaledHeight / 6)),
                  }}
                  data-testid={`field-${field.id}-option-${label}`}
                  data-option-label={label}
                  data-option-drag={isCustom && isSelected ? '1' : undefined}
                  title={isCustom && isSelected ? `Drag to reposition "${label}"` : undefined}
                >
                  <span
                    className={`inline-block rounded-full border flex-shrink-0 ${isDefault ? 'border-orange-500 bg-white' : 'border-gray-500 bg-white/80'}`}
                    style={{ width: dotSize, height: dotSize }}
                  >
                    {isDefault && (
                      <span
                        className="block rounded-full bg-orange-500"
                        style={{ width: '60%', height: '60%', margin: '20% auto 0' }}
                      />
                    )}
                  </span>
                  {!field.hide_option_labels && (
                    <span className="truncate text-gray-700 leading-none">{label}</span>
                  )}
                </div>
              );
            })}
          </>
        ) : scaledWidth > 40 && scaledHeight > 15 ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className="text-[9px] font-medium text-gray-600 truncate px-1 opacity-80"
              // Canvas zoom already scales position/width/height by `scale`
              // (see scaledWidth/scaledHeight above) — a font_size override
              // (in PDF points) follows the same convention so the box
              // visibly grows/shrinks in step with the Font size control,
              // instead of this placeholder staying a fixed 9px regardless.
              // The inline style overrides the fixed class only once a
              // recruiter actually sets an override; unset fields keep the
              // exact same default 9px they always have.
              style={field.font_size ? { fontSize: Math.max(6, field.font_size * scale) } : undefined}
            >
              {field.label.length > 15 ? field.label.substring(0, 12) + '...' : field.label}
            </span>
          </div>
        ) : null}
      </div>
      
      {/* Required indicator */}
      {field.required && (
        <div className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-red-500 rounded-full border border-white" title="Required field" />
      )}
      
      {/* Conditional indicator */}
      {field.condition && field.condition.field_id && (
        <div className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-purple-500 rounded-full border border-white" title="Has conditional logic" />
      )}
      
      {/* Resize handles - only show when selected */}
      {isSelected && (
        <>
          {/* Corner resize handles */}
          <div 
            data-resize="se"
            onMouseDown={(e) => handleResizeStart(e, 'se')}
            className="absolute -bottom-1.5 -right-1.5 w-3 h-3 bg-[#f97316] rounded-full cursor-se-resize border border-white shadow-sm hover:scale-125 transition-transform"
          />
          <div 
            data-resize="sw"
            onMouseDown={(e) => handleResizeStart(e, 'sw')}
            className="absolute -bottom-1.5 -left-1.5 w-3 h-3 bg-[#f97316] rounded-full cursor-sw-resize border border-white shadow-sm hover:scale-125 transition-transform"
          />
          <div 
            data-resize="ne"
            onMouseDown={(e) => handleResizeStart(e, 'ne')}
            className="absolute -top-1.5 -right-1.5 w-3 h-3 bg-[#f97316] rounded-full cursor-ne-resize border border-white shadow-sm hover:scale-125 transition-transform"
          />
          <div 
            data-resize="nw"
            onMouseDown={(e) => handleResizeStart(e, 'nw')}
            className="absolute -top-1.5 -left-1.5 w-3 h-3 bg-[#f97316] rounded-full cursor-nw-resize border border-white shadow-sm hover:scale-125 transition-transform"
          />
          
          {/* Edge resize handles */}
          <div 
            data-resize="e"
            onMouseDown={(e) => handleResizeStart(e, 'e')}
            className="absolute top-1/2 -right-1 w-2 h-6 -translate-y-1/2 bg-[#f97316]/80 rounded-full cursor-e-resize hover:bg-[#f97316] transition-colors"
          />
          <div 
            data-resize="s"
            onMouseDown={(e) => handleResizeStart(e, 's')}
            className="absolute -bottom-1 left-1/2 w-6 h-2 -translate-x-1/2 bg-[#f97316]/80 rounded-full cursor-s-resize hover:bg-[#f97316] transition-colors"
          />
        </>
      )}
    </div>
  );
};

export default function TemplateEditorPage() {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { getTenantPath, user } = useAuth();
  // Tenant-level default currency symbol — falls back to "£" so UK-based
  // tenants get the right symbol without any setup. Surfaces in the
  // ``inferCurrencyFromLabel`` calls when the recruiter adds a money-shaped
  // number field.
  const tenantDefaultCurrency = user?.tenant?.settings?.default_currency_symbol || '£';
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [pdfInfo, setPdfInfo] = useState({ pages: 0, width: 612, height: 792, has_pdf: false });
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [uploading, setUploading] = useState(false);
  // Cache-buster for PDF viewer — file_url stays the same across replaces,
  // so we append ?v=<timestamp> to force pdf.js to re-fetch.
  const [pdfCacheBust, setPdfCacheBust] = useState(0);
  // Inline-rename state for the template title
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [detecting, setDetecting] = useState(false);
  const [detectedFields, setDetectedFields] = useState(null);
  const [showDetectedPreview, setShowDetectedPreview] = useState(false);
  const [draggedFieldType, setDraggedFieldType] = useState(null);
  const [entityType, setEntityType] = useState('candidate');  // Entity type state
  
  // Snap-to-align state
  const [snapEnabled, setSnapEnabled] = useState(false);
  const [snapGuides, setSnapGuides] = useState({ horizontal: [], vertical: [] });
  const [gridSize, setGridSize] = useState(DEFAULT_GRID_SIZE);
  
  // Dynamic Bullhorn fields
  const [bullhornFields, setBullhornFields] = useState([]);
  const [loadingFields, setLoadingFields] = useState(false);
  const [fieldSearchQuery, setFieldSearchQuery] = useState('');
  const [mappingScope, setMappingScope] = useState('all'); // 'all' | 'Placement' | 'Candidate' | 'Job Order' | 'Client Company'
  
  // Test Field Mapping dialog
  const [testMappingOpen, setTestMappingOpen] = useState(false);
  const [testBullhornId, setTestBullhornId] = useState('');
  const [testMappingLoading, setTestMappingLoading] = useState(false);
  const [testMappingResult, setTestMappingResult] = useState(null);
  const [testMappingError, setTestMappingError] = useState('');
  const [testSearchQuery, setTestSearchQuery] = useState('');
  const [testSearchResults, setTestSearchResults] = useState([]);
  const [testSearching, setTestSearching] = useState(false);
  const [testSelectedName, setTestSelectedName] = useState('');
  
  // Bullhorn file types for upload tagging
  const [bullhornFileTypes, setBullhornFileTypes] = useState([]);
  const [selectedFileType, setSelectedFileType] = useState(null);
  const [requiresApproval, setRequiresApproval] = useState(false);
  // Whether the completed PDF uploads to the Bullhorn Files tab at all
  // (default on — matches today's always-upload behavior), and if it does,
  // whether it's tagged Public or Private there.
  const [bullhornFlowBack, setBullhornFlowBack] = useState(true);
  const [bullhornFileVisibility, setBullhornFileVisibility] = useState(null);
  const [loadingFileTypes, setLoadingFileTypes] = useState(false);
  // Per-template candidate-facing settings
  const [candidateInstructions, setCandidateInstructions] = useState('');
  const [templateMode, setTemplateMode] = useState('form'); // 'form' | 'attachment_only'
  // Delivery mode controls how the document reaches the recipient.
  // - interactive:   recipient gets a secure link, fills + signs in-browser (default)
  // - pdf_attachment: recipient gets the PDF as an email attachment, no link,
  //                  document is auto-completed + uploaded to Bullhorn Files tab.
  // Useful for client-side "for your records" sends (e.g. Terms of Business).
  // The recruiter can override this per-send on the Send screen.
  const [deliveryMode, setDeliveryMode] = useState('interactive');
  // Placement-only per-party routing. A placement template can produce
  // up to TWO documents on send — one for the candidate and one for the
  // contact — each with its own delivery_mode. Ignored for non-placement
  // templates (which use the single ``deliveryMode`` above).
  const [sendToCandidate, setSendToCandidate] = useState(true);
  const [sendToContact, setSendToContact] = useState(false);
  const [candidateDeliveryMode, setCandidateDeliveryMode] = useState('interactive');
  const [contactDeliveryMode, setContactDeliveryMode] = useState('pdf_attachment');
  // Bullhorn Credential mapping (candidate / placement templates only).
  // When enabled with a credential picked from the tenant's Bullhorn
  // Certification catalog, completing the document creates a new
  // CandidateCertification / PlacementCertification record on Bullhorn.
  const [credentialMappingEnabled, setCredentialMappingEnabled] = useState(false);
  const [credentialId, setCredentialId] = useState(null);
  const [credentialName, setCredentialName] = useState('');
  const [attachmentChecklist, setAttachmentChecklist] = useState([]);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  // "Informational only" — recruiter flag for documents the candidate
  // just needs to *read* (e.g. Welcome Pack, Privacy Notice).  When true
  // we render the document read-only on the candidate side, skip the
  // required-field gate, and skip the Bullhorn write-back. The doc
  // still gets attached to Bullhorn as a sent record.
  const [isInformational, setIsInformational] = useState(false);
  // Per-document override of the workspace-wide completion-notification
  // defaults (Settings → Notifications). Tri-state: null = inherit the
  // workspace setting, true/false = force this template's notification
  // on/off regardless of what the workspace default says.
  const [notifySenderOnCompletion, setNotifySenderOnCompletion] = useState(null);
  const [notifyRecipientOnCompletion, setNotifyRecipientOnCompletion] = useState(null);
  // Opt-in — attaches the completed document + Certificate of Completion
  // to the recipient's completion-confirmation email above. Off by default
  // for every template; aimed at client-facing contracts first.
  const [sendCompletedCopyToRecipient, setSendCompletedCopyToRecipient] = useState(false);
  // Per-document override of the workspace-wide default reminder cadence
  // (Settings → Notifications → Default reminder cadence). null = inherit
  // the workspace default, a number = override this template's outstanding
  // documents onto that many days between automated reminders instead.
  const [reminderDays, setReminderDays] = useState(null);
  // Manual "about N minutes" shown to candidates until the template has enough completions for a measured median.
  const [estimatedMinutes, setEstimatedMinutes] = useState('');
  // Per-document override of the workspace's completion-notification CC
  // list (Settings → Notifications → Additional recipients). null = inherit
  // the workspace list, an array (including []) = override it for this
  // template's completion emails. notificationCcInput holds the raw
  // textarea text while an override is active — parsed on Save, same as
  // the workspace setting's own CC field.
  const [notificationCcEmails, setNotificationCcEmails] = useState(null);
  const [notificationCcInput, setNotificationCcInput] = useState('');
  // Undo/Redo history
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedoAction = useRef(false);
  
  const canvasRef = useRef(null);
  const pdfScrollRef = useRef(null);

  useEffect(() => {
    loadTemplate();
  }, [templateId]);

  useEffect(() => {
    if (template?.id) {
      loadPdfInfo();
    }
  }, [template]);

  // Auto-fit PDF width to the canvas scroll container on first load.
  // The PDF page itself renders at a fixed `pdfInfo.width * scale` —
  // widening the surrounding container alone just adds whitespace, so
  // we compute the maximum scale that fits the available width (minus
  // padding for field handles) and apply it once when the PDF lands.
  const hasAutoFitRef = useRef(false);
  useEffect(() => {
    if (!pdfInfo.has_pdf) return;
    if (hasAutoFitRef.current) return;
    const container = pdfScrollRef.current;
    if (!container || !pdfInfo.width) return;
    const available = container.clientWidth - 32; // 16px padding either side
    if (available <= 0) return;
    const fit = Math.min(1.5, Math.max(0.5, available / pdfInfo.width));
    setScale(fit);
    hasAutoFitRef.current = true;
  }, [pdfInfo.has_pdf, pdfInfo.width]);

  const fitToWidth = () => {
    const container = pdfScrollRef.current;
    if (!container || !pdfInfo.width) return;
    const available = container.clientWidth - 32;
    if (available <= 0) return;
    setScale(Math.min(1.5, Math.max(0.5, available / pdfInfo.width)));
  };
  
  // Load Bullhorn fields when entity type changes
  useEffect(() => {
    loadBullhornFields(entityType);
    loadBullhornFileTypes(entityType);
  }, [entityType]);

  // One-time backfill: any dropdown / radio / checkbox-group field that
  // is mapped to a Bullhorn picklist but has no `bullhorn_picklist_options`
  // (because it was saved before the picklist feature shipped) — pull the
  // option labels now so the editor + candidate UI display labels not IDs.
  useEffect(() => {
    if (!fields?.length) return;
    const need = fields.filter(f =>
      ['dropdown', 'radio_group', 'checkbox_group'].includes(f.field_type)
      && (f.bullhorn_source_field || f.mapped_bullhorn_field)
      && !(Array.isArray(f.bullhorn_picklist_options) && f.bullhorn_picklist_options.length)
    );
    if (!need.length) return;
    const seen = new Set();
    need.forEach((f) => {
      const bhField = f.bullhorn_source_field || f.mapped_bullhorn_field;
      const cacheKey = `${entityType}::${bhField}`;
      if (seen.has(cacheKey)) return;
      seen.add(cacheKey);
      axios.get(`${API_URL}/bullhorn/field-options`, {
        params: { entity: entityType || 'candidate', field: bhField },
      }).then(({ data }) => {
        if (!data?.is_picklist || !Array.isArray(data.options) || !data.options.length) return;
        setFields((prev) => prev.map((cur) => {
          if ((cur.bullhorn_source_field || cur.mapped_bullhorn_field) !== bhField) return cur;
          if (Array.isArray(cur.bullhorn_picklist_options) && cur.bullhorn_picklist_options.length) return cur;
          return {
            ...cur,
            options: data.options.map(o => o.value).filter(Boolean),
            bullhorn_picklist_options: data.options,
            bullhorn_picklist_multi: !!data.multi,
            bullhorn_picklist_entity_backed: !!data.entity_backed,
          };
        }));
      }).catch(() => { /* silent — recruiter can re-map manually */ });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields?.length, entityType]);

  const loadBullhornFileTypes = async (type) => {
    setLoadingFileTypes(true);
    try {
      const response = await bullhornApi.getFileTypes(type);
      const fileTypes = response.data.file_types || [];
      setBullhornFileTypes(fileTypes);
    } catch (error) {
      console.error('Failed to load Bullhorn file types:', error);
      // Set defaults
      setBullhornFileTypes([
        { value: 'CV', label: 'CV' },
        { value: 'Onboarding Document', label: 'Onboarding Document' },
        { value: 'Contract Signed', label: 'Contract Signed' },
        { value: 'Other', label: 'Other' }
      ]);
    } finally {
      setLoadingFileTypes(false);
    }
  };
  
  const loadBullhornFields = async (type) => {
    setLoadingFields(true);
    try {
      const response = await bullhornApi.getEntityFields(type);
      const fieldsData = response.data.fields || [];
      // Format for dropdown
      const formatted = fieldsData.map(f => ({
        value: f.name,
        label: f.label || f.name,
        dataType: f.dataType,
        is_picklist: !!f.is_picklist,
        is_composite: !!f.is_composite,
        composite_parts: f.composite_parts || null,
        entityGroup: f.entity_group || null,
        disabled: false
      }));
      // Bullhorn commonly marks children of its composite `address` field as
      // hidden (and some corporation schemas omit them from meta entirely).
      // The legacy picker always exposed these valid dot-path mappings, so
      // merge the canonical mappings into the live schema instead of replacing
      // them wholesale. Live metadata still wins for labels and data types.
      const canonical = type === 'placement'
        ? BULLHORN_PLACEMENT_FIELDS
        : type === 'contact'
          ? BULLHORN_CONTACT_FIELDS
          : BULLHORN_CANDIDATE_FIELDS;
      const seen = new Set(formatted.map(field => field.value));
      // A canonical fallback entry (added because live meta omitted or hid
      // it) has no entityGroup of its own. The scope-tab counter buckets
      // anything without one under "Placement" — correct by coincidence
      // for placement's own un-prefixed fields, but wrong for candidate/
      // contact templates, where it mislabels e.g. the contact's hidden
      // address sub-fields as "Placement" fields.
      const canonicalGroup = type === 'placement' ? 'Placement' : type === 'contact' ? 'ClientContact' : 'Candidate';
      const merged = [
        ...formatted,
        ...canonical
          .filter(field => field.value !== 'none' && !seen.has(field.value))
          .map(field => ({ ...field, entityGroup: field.entityGroup || canonicalGroup })),
      ];
      setBullhornFields(merged);
      
      // For placement templates, default to "Placement" scope so recruiters see the most relevant fields first
      if (entityType === 'placement' && merged.some(f => f.entityGroup)) {
        setMappingScope('Placement');
      } else {
        setMappingScope('all');
      }
    } catch (error) {
      console.error('Failed to load Bullhorn fields:', error);
      // Fall back to defaults
      const defaults = entityType === 'contact' ? BULLHORN_CONTACT_FIELDS : BULLHORN_CANDIDATE_FIELDS;
      setBullhornFields(defaults);
    } finally {
      setLoadingFields(false);
    }
  };

  const deleteField = useCallback((fieldId) => {
    setFields(prev => {
      const result = removeFieldAndConditionReferences(prev, fieldId);
      if (result.cleared) {
        toast.info(`Removed ${result.cleared} conditional rule${result.cleared === 1 ? '' : 's'} that depended on this field.`);
      }
      return result.fields;
    });
    setSelectedField(prev => prev === fieldId ? null : prev);
  }, []);

  // When the user clicks a field that's already mapped to a Bullhorn
  // field in a DIFFERENT entity group than the current scope tab, jump
  // the scope to match. Without this the user opens a Placement template,
  // clicks a field mapped to e.g. `clientCorporation.name` (Client Company
  // group), and the dropdown stays on "Placement" scope — visually it
  // still resolves (we keep the active option visible) but the tab is
  // misleading. Auto-switching makes the context clear and lets the user
  // browse sibling fields in the same group right away.
  useEffect(() => {
    if (!selectedField || !bullhornFields.length) return;
    const sel = fields.find(f => f.id === selectedField);
    if (!sel) return;
    const mapping = sel.bullhorn_source_field || sel.mapped_bullhorn_field;
    if (!mapping) return;
    const opt = bullhornFields.find(f => f.value === mapping);
    if (!opt?.entityGroup) return;
    if (mappingScope !== 'all' && mappingScope !== opt.entityGroup) {
      setMappingScope(opt.entityGroup);
    }
    // Only run on selection change — don't loop when user manually picks scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedField, bullhornFields]);

  // Keyboard delete support
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedField) {
        // Don't delete if user is typing in an input
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
        e.preventDefault();
        deleteField(selectedField);
        toast.success('Field deleted');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedField, deleteField]);
  
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedoAction.current = true;
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setFields(JSON.parse(JSON.stringify(history[newIndex])));
      toast.info('Undo', { duration: 1000 });
    }
  }, [historyIndex, history]);
  
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedoAction.current = true;
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setFields(JSON.parse(JSON.stringify(history[newIndex])));
      toast.info('Redo', { duration: 1000 });
    }
  }, [historyIndex, history]);

  // Undo/Redo keyboard shortcuts
  useEffect(() => {
    const handleUndoRedo = (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlKey = isMac ? e.metaKey : e.ctrlKey;
      
      if (ctrlKey && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((ctrlKey && e.key === 'z' && e.shiftKey) || (ctrlKey && e.key === 'y')) {
        e.preventDefault();
        handleRedo();
      }
    };
    
    window.addEventListener('keydown', handleUndoRedo);
    return () => window.removeEventListener('keydown', handleUndoRedo);
  }, [historyIndex, history, handleUndo, handleRedo]);

  // Keyboard arrow key support for fine-tuning field positions
  useEffect(() => {
    const handleArrowKeys = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (!selectedField) return;
      
      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (!arrowKeys.includes(e.key)) return;
      
      e.preventDefault();
      
      const moveAmount = e.shiftKey ? KEYBOARD_MOVE_LARGE : KEYBOARD_MOVE_SMALL;
      
      setFields(prev => {
        const field = prev.find(f => f.id === selectedField);
        if (!field) return prev;
        
        let newX = field.position_x;
        let newY = field.position_y;
        
        switch (e.key) {
          case 'ArrowUp': newY = Math.max(0, field.position_y - moveAmount); break;
          case 'ArrowDown': newY = field.position_y + moveAmount; break;
          case 'ArrowLeft': newX = Math.max(0, field.position_x - moveAmount); break;
          case 'ArrowRight': newX = field.position_x + moveAmount; break;
          default: return prev;
        }
        
        return prev.map(f => 
          f.id === selectedField 
            ? { ...f, position_x: newX, position_y: newY }
            : f
        );
      });
    };
    
    window.addEventListener('keydown', handleArrowKeys);
    return () => {
      window.removeEventListener('keydown', handleArrowKeys);
    };
  }, [selectedField]);
  
  // Track field changes for undo/redo (debounced to avoid too many history entries)
  const historyTimeout = useRef(null);
  useEffect(() => {
    if (isUndoRedoAction.current) {
      isUndoRedoAction.current = false;
      return;
    }
    
    // Clear any pending history save
    if (historyTimeout.current) {
      clearTimeout(historyTimeout.current);
    }
    
    // Debounce history saves to group rapid changes
    historyTimeout.current = setTimeout(() => {
      if (fields.length > 0 || history.length > 0) {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(JSON.parse(JSON.stringify(fields)));
        // Keep last 50 states
        if (newHistory.length > 50) newHistory.shift();
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      }
    }, 300);
    
    return () => {
      if (historyTimeout.current) clearTimeout(historyTimeout.current);
    };
  }, [fields]);
  
  const loadTemplate = async () => {
    try {
      const response = await templatesApi.get(templateId);
      setTemplate(response.data);
      setFields(response.data.fields || []);
      setEntityType(response.data.entity_type || 'candidate');  // Load entity type
      setSelectedFileType(response.data.bullhorn_file_type || null);
      setBullhornFlowBack(response.data.bullhorn_flow_back !== false);
      setBullhornFileVisibility(response.data.bullhorn_file_visibility || null);
      setRequiresApproval(response.data.requires_approval || false);
      // Falls back to the description entered when the template was created —
      // same concept, same field from the user's point of view. A template
      // that's only ever had its description set should show that here
      // ready to confirm/edit, not a blank box.
      setCandidateInstructions(response.data.candidate_instructions || response.data.description || '');
      setTemplateMode(response.data.template_mode || 'form');
      setDeliveryMode(response.data.delivery_mode || 'interactive');
      setIsInformational(response.data.is_informational || false);
      setNotifySenderOnCompletion(response.data.notify_sender_on_completion ?? null);
      setNotifyRecipientOnCompletion(response.data.notify_recipient_on_completion ?? null);
      setSendCompletedCopyToRecipient(!!response.data.send_completed_copy_to_recipient);
      setReminderDays(response.data.reminder_days ?? null);
      setEstimatedMinutes(response.data.estimated_minutes == null ? '' : String(response.data.estimated_minutes));
      setNotificationCcEmails(response.data.notification_cc_emails ?? null);
      setNotificationCcInput((response.data.notification_cc_emails || []).join(', '));
      setSendToCandidate(!!response.data.send_to_candidate);
      setSendToContact(!!response.data.send_to_contact);
      setCandidateDeliveryMode(response.data.candidate_delivery_mode || 'interactive');
      setContactDeliveryMode(response.data.contact_delivery_mode || 'pdf_attachment');
      setCredentialMappingEnabled(!!response.data.credential_mapping_enabled);
      setCredentialId(response.data.credential_id ?? null);
      setCredentialName(response.data.credential_name || '');
      setAttachmentChecklist(response.data.attachment_checklist || []);
    } catch (error) {
      toast.error('Failed to load template');
      navigate(getTenantPath('/templates'));
    } finally {
      setLoading(false);
    }
  };

  const loadPdfInfo = async () => {
    try {
      const response = await axios.get(`${API_URL}/pdf-info/${templateId}`);
      const data = response.data;
      setPdfInfo({
        has_pdf: data.has_pdf,
        pages: data.page_count || 0,
        width: data.dimensions?.width || 612,
        height: data.dimensions?.height || 792,
        file_url: data.file_url
      });
    } catch (error) {
      console.error('Failed to load PDF info:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    // Reset the input value so picking the same file again still triggers change.
    e.target.value = '';
    if (!file) return;

    const name = file.name.toLowerCase();
    if (!name.endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    const invalidConditions = findInvalidConditionReferences(fields);
    if (invalidConditions.length) {
      const first = invalidConditions[0];
      setSelectedField(first.field.id);
      toast.error(`"${first.field.label || 'Field'}" references a conditional field that no longer exists. Repair the rule before replacing the PDF.`);
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', template.name);
      formData.append('description', template.description || '');
      formData.append('fields', JSON.stringify(fields));
      formData.append('file', file);

      await templatesApi.update(templateId, formData);
      toast.success('PDF replaced successfully');
      // Bust pdf.js / browser cache — file_url stays the same, so without a
      // cache-buster the <Document> component keeps rendering the old PDF.
      setPdfCacheBust(Date.now());
      await loadTemplate();
      await loadPdfInfo();
    } catch (error) {
      toast.error('Failed to upload PDF');
    } finally {
      setUploading(false);
    }
  };

  const handleAIDetection = async (useAi = true) => {
    if (!pdfInfo.has_pdf) {
      toast.error('Please upload a PDF document first');
      return;
    }

    setDetecting(true);
    toast.info('Starting AI field detection...', { duration: 3000 });
    
    try {
      // Start the detection task
      const startResponse = await templatesApi.detectFields(templateId, useAi);
      const responseData = startResponse.data;
      
      // Check if we got an immediate result (direct response with fields)
      if (responseData.success && responseData.fields?.length > 0) {
        setDetectedFields(responseData.fields);
        setShowDetectedPreview(true);
        toast.success(`Detected ${responseData.count} fields using AI`);
        setDetecting(false);
        return;
      }
      
      // Check if it's a task response that needs polling
      if (responseData.task_id) {
        const { task_id, status } = responseData;
        
        if (status === 'completed' && responseData.fields) {
          // Task already completed with fields
          setDetectedFields(responseData.fields);
          setShowDetectedPreview(true);
          toast.success(`Detected ${responseData.count} fields`);
          setDetecting(false);
          return;
        }
        
        // Poll for results
        toast.info('Analyzing document with AI... This may take up to 60 seconds.', { duration: 5000 });
        
        const pollInterval = 2000; // Poll every 2 seconds
        const maxPolls = 90; // Max 3 minutes of polling
        let polls = 0;
        
        const pollForResult = async () => {
          try {
            const statusResponse = await templatesApi.getDetectionStatus(templateId, task_id);
            const result = statusResponse.data;
            
            if (result.status === 'completed') {
              setDetecting(false);
              if (result.fields?.length > 0) {
                setDetectedFields(result.fields);
                setShowDetectedPreview(true);
                toast.success(`Detected ${result.count} fields using ${result.method === 'ai' ? 'AI' : 'pattern matching'}`);
              } else {
                toast.info('No fields detected. Try adding fields manually.');
              }
              return;
            } else if (result.status === 'failed') {
              setDetecting(false);
              toast.error(result.error || 'AI detection failed. Please try again.');
              return;
            }
            
            // Still running, continue polling
            polls++;
            if (polls < maxPolls) {
              setTimeout(pollForResult, pollInterval);
            } else {
              setDetecting(false);
              toast.error('Detection timed out. Please try again.');
            }
          } catch (pollError) {
            console.error('Poll error:', pollError);
            setDetecting(false);
            toast.error('Failed to get detection status');
          }
        };
        
        // Start polling after a short delay
        setTimeout(pollForResult, pollInterval);
      } else {
        // No task_id and no fields - something unexpected
        setDetecting(false);
        toast.info('No fields detected. Try adding fields manually.');
      }
      
    } catch (error) {
      console.error('Field detection failed:', error);
      setDetecting(false);
      const errorMessage = error.response?.data?.detail || 'AI detection failed';
      toast.error(errorMessage);
    }
  };

  const applyDetectedFields = (mode = 'replace') => {
    if (!detectedFields) return;

    // Run currency inference over AI-detected number fields. Mirrors
    // the manual ``addField`` flow so detected "Day rate" / "Salary"
    // fields land with £ already set, no manual click needed.
    const withCurrency = detectedFields.map((f) => {
      if (f.field_type !== 'number' || f.currency_symbol) return f;
      const inferred = inferCurrencyFromLabel(f.label, tenantDefaultCurrency);
      return inferred ? {
        ...f,
        currency_symbol: inferred.symbol,
        currency_position: inferred.position,
        currency_format_money: inferred.formatMoney,
      } : f;
    });

    if (mode === 'replace') {
      const result = reconcileFieldConditions(fields, withCurrency);
      setFields(result.fields);
      if (result.preserved) {
        toast.success(`Preserved ${result.preserved} conditional rule${result.preserved === 1 ? '' : 's'} while replacing fields.`);
      }
      if (result.dropped) {
        toast.warning(`${result.dropped} conditional rule${result.dropped === 1 ? '' : 's'} could not be matched and must be configured again.`);
      }
    } else {
      const existingIds = new Set(fields.map(f => f.id));
      const newFields = withCurrency.filter(f => !existingIds.has(f.id));
      setFields([...fields, ...newFields]);
    }
    
    setShowDetectedPreview(false);
    setDetectedFields(null);
    toast.success('Fields applied to template');
  };

  const cancelDetectedFields = () => {
    setShowDetectedPreview(false);
    setDetectedFields(null);
  };

  const addField = (type, dropPosition = null) => {
    const fieldCount = fields.filter(f => f.page === currentPage).length;
    const newField = {
      id: `field_${Date.now()}`,
      label: `New ${FIELD_TYPES.find(f => f.value === type)?.label || type}`,
      field_type: type,
      required: false,
      position_x: dropPosition?.x || 150,
      position_y: dropPosition?.y || (100 + (fieldCount * 30)),
      width: type === 'signature' ? 180 : (type === 'checkbox' ? 20 : (type === 'radio_group' ? 200 : 150)),
      height: type === 'signature' ? 45 : (type === 'checkbox' ? 20 : (type === 'radio_group' ? 80 : 24)),
      page: currentPage,
      options: type === 'dropdown' ? ['Option 1', 'Option 2'] : (type === 'radio_group' ? ['Option 1', 'Option 2', 'Option 3'] : null),
      mapped_bullhorn_field: '',
      placeholder: '',
      // New field-level configuration options
      candidate_editable: true,
      write_back_to_bullhorn: false,
      bullhorn_source_field: '',
      // Conditional logic
      condition: null, // { field_id, operator, value }
    };
    // Number fields: auto-detect a currency / unit symbol from the
    // default label so recruiters don't have to remember to set one.
    // Skipped on the generic "New Number" default — the label has to
    // match a money/percentage keyword for inference to fire.
    if (type === 'number') {
      const inferred = inferCurrencyFromLabel(newField.label, tenantDefaultCurrency);
      if (inferred) {
        newField.currency_symbol = inferred.symbol;
        newField.currency_position = inferred.position;
        newField.currency_format_money = inferred.formatMoney;
      }
    }
    setFields([...fields, newField]);
    setSelectedField(newField.id);
  };

  // Handle drag start from field type buttons
  const handleFieldTypeDragStart = (e, type) => {
    e.dataTransfer.setData('fieldType', type);
    setDraggedFieldType(type);
  };

  // Handle drop on canvas
  const handleCanvasDrop = (e) => {
    e.preventDefault();
    const fieldType = e.dataTransfer.getData('fieldType');
    if (!fieldType) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;

    addField(fieldType, { x: Math.max(0, x - 50), y: Math.max(0, y - 10) });
    setDraggedFieldType(null);
  };

  const handleCanvasDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const updateField = useCallback((fieldId, updates) => {
    setFields(prev => prev.map(f => f.id === fieldId ? { ...f, ...updates } : f));
  }, []);


  const handleTestMapping = async () => {
    if (!testBullhornId.trim()) {
      setTestMappingError('Please pick a Bullhorn record to test with.');
      return;
    }
    setTestMappingLoading(true);
    setTestMappingError('');
    setTestMappingResult(null);
    try {
      const response = await templatesApi.testMapping(templateId, testBullhornId.trim(), entityType);
      setTestMappingResult(response.data);
    } catch (err) {
      const msg = typeof err.response?.data?.detail === 'string'
        ? err.response.data.detail
        : 'Failed to run test mapping. Check that Bullhorn is connected and the ID exists.';
      setTestMappingError(msg);
    } finally {
      setTestMappingLoading(false);
    }
  };

  const handleTestSearch = async () => {
    if (testSearchQuery.trim().length < 2) return;
    setTestSearching(true);
    setTestSearchResults([]);
    try {
      let results = [];
      if (entityType === 'contact') {
        const r = await bullhornApi.searchContacts(testSearchQuery.trim());
        results = (r.data.contacts || []).map(c => ({
          id: c.id,
          name: c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || `Contact #${c.id}`,
          email: c.email,
          subtitle: c.email || c.companyName || '',
        }));
      } else if (entityType === 'placement') {
        const r = await bullhornApi.searchPlacements(testSearchQuery.trim());
        results = (r.data.placements || []).map(p => ({
          id: p.id,
          name: p.candidate?.name || `Placement #${p.id}`,
          email: p.candidate?.email,
          subtitle: [p.jobOrder?.title, p.company?.name].filter(Boolean).join(' @ '),
        }));
      } else {
        const r = await bullhornApi.searchCandidates(testSearchQuery.trim());
        results = (r.data.candidates || []).map(c => ({
          id: c.id,
          name: c.name || `${c.firstName || ''} ${c.lastName || ''}`.trim() || `Candidate #${c.id}`,
          email: c.email,
          subtitle: c.email || c.occupation || '',
        }));
      }
      setTestSearchResults(results);
    } catch (err) {
      toast.error(`Failed to search ${entityType}s`);
    } finally {
      setTestSearching(false);
    }
  };

  const selectTestResult = (result) => {
    setTestBullhornId(String(result.id));
    setTestSelectedName(result.name);
    setTestSearchResults([]);
    setTestSearchQuery('');
    setTestMappingResult(null);
    setTestMappingError('');
  };

  const openTestMappingDialog = () => {
    setTestMappingOpen(true);
    setTestMappingResult(null);
    setTestMappingError('');
    setTestBullhornId('');
    setTestSelectedName('');
    setTestSearchQuery('');
    setTestSearchResults([]);
  };

  const handleSave = async () => {
    const invalidConditions = findInvalidConditionReferences(fields);
    if (invalidConditions.length) {
      const first = invalidConditions[0];
      setSelectedField(first.field.id);
      toast.error(`"${first.field.label || 'Field'}" references a conditional field that no longer exists. Select its “When this field” value before saving.`);
      return;
    }
    // Parsed up front (before setSaving) so a bad email aborts the save
    // the same way an invalid condition reference does above, rather than
    // failing mid-save.
    let parsedCcEmails = null;
    if (notificationCcEmails != null) {
      try {
        parsedCcEmails = parseCcEmails(notificationCcInput);
      } catch (ccError) {
        toast.error(ccError.message);
        return;
      }
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', template.name);
      // Kept in sync with candidate_instructions below — Description (set at
      // template creation) and the "shown to the candidate" text here are
      // the same concept from the user's point of view, so editing one here
      // updates both rather than leaving the original creation-time
      // description stale.
      formData.append('description', candidateInstructions || '');
      formData.append('fields', JSON.stringify(fields));
      formData.append('entity_type', entityType);  // Save entity type
      formData.append('bullhorn_file_type', selectedFileType || '');
      formData.append('bullhorn_flow_back', bullhornFlowBack ? 'true' : 'false');
      formData.append('bullhorn_file_visibility', bullhornFileVisibility || '');
      formData.append('requires_approval', requiresApproval ? 'true' : 'false');
      formData.append('candidate_instructions', candidateInstructions || '');
      formData.append('template_mode', templateMode || 'form');
      formData.append('delivery_mode', deliveryMode || 'interactive');
      formData.append('is_informational', isInformational ? 'true' : 'false');
      formData.append('notify_sender_on_completion', notifySenderOnCompletion == null ? 'inherit' : (notifySenderOnCompletion ? 'true' : 'false'));
      formData.append('notify_recipient_on_completion', notifyRecipientOnCompletion == null ? 'inherit' : (notifyRecipientOnCompletion ? 'true' : 'false'));
      formData.append('send_completed_copy_to_recipient', sendCompletedCopyToRecipient ? 'true' : 'false');
      formData.append('reminder_days', reminderDays == null ? '' : String(reminderDays));
      formData.append('estimated_minutes', estimatedMinutes === '' ? '' : String(estimatedMinutes));
      formData.append('notification_cc_emails', notificationCcEmails == null ? '' : JSON.stringify(parsedCcEmails));
      formData.append('send_to_candidate', sendToCandidate ? 'true' : 'false');
      formData.append('send_to_contact', sendToContact ? 'true' : 'false');
      formData.append('candidate_delivery_mode', candidateDeliveryMode || 'interactive');
      formData.append('contact_delivery_mode', contactDeliveryMode || 'pdf_attachment');
      formData.append('credential_mapping_enabled', credentialMappingEnabled ? 'true' : 'false');
      if (credentialId != null) formData.append('credential_id', String(credentialId));
      else formData.append('credential_id', '');
      formData.append('credential_name', credentialName || '');
      formData.append('attachment_checklist', JSON.stringify(attachmentChecklist || []));
      
      await templatesApi.update(templateId, formData);
      if (parsedCcEmails != null) setNotificationCcInput(parsedCcEmails.join(', '));
      toast.success('Template saved successfully');
    } catch (error) {
      toast.error('Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setPdfInfo(prev => ({ ...prev, pages: numPages }));
  };

  // Get Bullhorn fields - use dynamic data if loaded, otherwise defaults
  // Returns with frequently used fields marked
  // Supports search filtering
  const getBullhornFields = (searchQuery = '') => {
    const frequentlyUsedNames = new Set(FREQUENTLY_USED_FIELDS.map(f => f.value));
    const query = searchQuery.toLowerCase().trim();
    
    // Filter function for search
    const matchesSearch = (field) => {
      if (!query) return true;
      const label = (field.label || '').toLowerCase();
      const value = (field.value || '').toLowerCase();
      return label.includes(query) || value.includes(query);
    };
    
    if (bullhornFields.length > 0) {
      // Split into frequently used and others
      const frequent = [];
      const others = [];
      
      // Bullhorn field that this template field is already mapped to. We
      // must never filter this option out — otherwise the Select component
      // can't find a matching <SelectItem> for its `value` and the dropdown
      // renders BLANK even though a mapping exists (e.g. a Placement
      // template field mapped to `clientCorporation.name` while the scope
      // tab is on "Placement").
      const activeMapping = selectedFieldData
        ? (selectedFieldData.bullhorn_source_field || selectedFieldData.mapped_bullhorn_field || '')
        : '';

      // Only offer Bullhorn fields whose data type this HyreDocs field can
      // actually carry (e.g. a Date field → Timestamp/DateTime only). The
      // active mapping and fields with an unknown dataType are never hidden.
      const typeCompat = FIELD_TYPE_BULLHORN_COMPAT[selectedFieldData?.field_type];

      bullhornFields.forEach(field => {
        const isActive = activeMapping && field.value === activeMapping;
        if (!isActive && !matchesSearch(field)) return;
        // Apply entity scope filter only when browsing (no active search)
        // AND the field isn't the active mapping. Active mappings always
        // show so the Select can resolve its current value.
        if (!isActive && !query && mappingScope !== 'all' && field.entityGroup && field.entityGroup !== mappingScope) return;
        // Field-type compatibility filter.
        if (!isActive && typeCompat && field.dataType && !typeCompat(field)) return;

        if (frequentlyUsedNames.has(field.value)) {
          frequent.push({ ...field, isFrequent: true });
        } else {
          others.push(field);
        }
      });

      // Belt-and-suspenders: if the stored mapping value isn't present in the
      // loaded `bullhornFields` at all (e.g. a tenant whose Bullhorn entity
      // schema lost the field, or a legacy mapping using an old naming
      // convention) synthesise a placeholder so the Select can still resolve
      // and the recruiter can SEE what was mapped instead of a blank dropdown.
      if (activeMapping && !bullhornFields.some(f => f.value === activeMapping)) {
        others.unshift({
          value: activeMapping,
          label: activeMapping,
          entityGroup: null,
          disabled: false,
          isOrphaned: true,
        });
      }
      
      // Sort alphabetically by label (case-insensitive)
      const byLabel = (a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' });
      frequent.sort(byLabel);
      others.sort(byLabel);
      
      return { frequent, others, totalCount: bullhornFields.length };
    }
    
    // Fallback to hardcoded defaults
    let baseFields;
    if (entityType === 'placement') {
      baseFields = BULLHORN_PLACEMENT_FIELDS;
    } else if (entityType === 'contact') {
      baseFields = BULLHORN_CONTACT_FIELDS;
    } else {
      baseFields = BULLHORN_CANDIDATE_FIELDS;
    }
    
    const allFields = entityType === 'contact' 
      ? [...baseFields, ...BULLHORN_COMPANY_FIELDS.slice(1)]
      : baseFields;
    
    const frequent = [];
    const others = [];
    const typeCompat = FIELD_TYPE_BULLHORN_COMPAT[selectedFieldData?.field_type];
    const activeMapping = selectedFieldData
      ? (selectedFieldData.bullhorn_source_field || selectedFieldData.mapped_bullhorn_field || '')
      : '';

    allFields.forEach(field => {
      if (field.value === 'none') return; // Skip 'none' - we handle it separately
      const isActive = activeMapping && field.value === activeMapping;
      if (!isActive && !matchesSearch(field)) return;
      if (!isActive && typeCompat && field.dataType && !typeCompat(field)) return;

      if (frequentlyUsedNames.has(field.value)) {
        frequent.push({ ...field, isFrequent: true });
      } else {
        others.push(field);
      }
    });
    
    // Sort alphabetically by label (case-insensitive)
    const byLabel = (a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' });
    frequent.sort(byLabel);
    others.sort(byLabel);
    
    return { frequent, others, totalCount: allFields.length - 1 };
  };
  
  // Snap-to-align helper - calculates snapping for a field being moved
  const calculateSnap = (fieldId, newX, newY, fieldWidth, fieldHeight) => {
    if (!snapEnabled) return { x: newX, y: newY, guides: { horizontal: [], vertical: [] } };
    
    const otherFields = fields.filter(f => f.id !== fieldId && f.page === currentPage);
    const guides = { horizontal: [], vertical: [] };
    let snappedX = newX;
    let snappedY = newY;
    
    // Field edges
    const left = newX;
    const right = newX + fieldWidth;
    const top = newY;
    const bottom = newY + fieldHeight;
    const centerX = newX + fieldWidth / 2;
    const centerY = newY + fieldHeight / 2;
    
    // Check against other fields
    for (const other of otherFields) {
      const oLeft = other.position_x;
      const oRight = other.position_x + other.width;
      const oTop = other.position_y;
      const oBottom = other.position_y + other.height;
      const oCenterX = other.position_x + other.width / 2;
      const oCenterY = other.position_y + other.height / 2;
      
      // Vertical alignment (snap X)
      if (Math.abs(left - oLeft) < SNAP_THRESHOLD) {
        snappedX = oLeft;
        guides.vertical.push(oLeft);
      } else if (Math.abs(right - oRight) < SNAP_THRESHOLD) {
        snappedX = oRight - fieldWidth;
        guides.vertical.push(oRight);
      } else if (Math.abs(left - oRight) < SNAP_THRESHOLD) {
        snappedX = oRight;
        guides.vertical.push(oRight);
      } else if (Math.abs(right - oLeft) < SNAP_THRESHOLD) {
        snappedX = oLeft - fieldWidth;
        guides.vertical.push(oLeft);
      } else if (Math.abs(centerX - oCenterX) < SNAP_THRESHOLD) {
        snappedX = oCenterX - fieldWidth / 2;
        guides.vertical.push(oCenterX);
      }
      
      // Horizontal alignment (snap Y)
      if (Math.abs(top - oTop) < SNAP_THRESHOLD) {
        snappedY = oTop;
        guides.horizontal.push(oTop);
      } else if (Math.abs(bottom - oBottom) < SNAP_THRESHOLD) {
        snappedY = oBottom - fieldHeight;
        guides.horizontal.push(oBottom);
      } else if (Math.abs(top - oBottom) < SNAP_THRESHOLD) {
        snappedY = oBottom;
        guides.horizontal.push(oBottom);
      } else if (Math.abs(bottom - oTop) < SNAP_THRESHOLD) {
        snappedY = oTop - fieldHeight;
        guides.horizontal.push(oTop);
      } else if (Math.abs(centerY - oCenterY) < SNAP_THRESHOLD) {
        snappedY = oCenterY - fieldHeight / 2;
        guides.horizontal.push(oCenterY);
      }
    }
    
    // Snap to grid if no field alignment (use configurable grid size)
    if (guides.vertical.length === 0 && gridSize > 1) {
      snappedX = Math.round(snappedX / gridSize) * gridSize;
    }
    if (guides.horizontal.length === 0 && gridSize > 1) {
      snappedY = Math.round(snappedY / gridSize) * gridSize;
    }
    
    return { x: snappedX, y: snappedY, guides };
  };
  
  // Snap a resize gesture. Only the edges actively being dragged (encoded
  // in resizeDirection, e.g. "e", "s", "se", "nw") are candidates for
  // snapping; the anchored edges stay put. We snap the dragged edge to
  // ALIGN with other fields' edges (e.g. drag E-handle → snap right edge
  // to other fields' right OR left edges), which is what gives the user
  // "same-width-as-the-row-above" behaviour. Returns adjusted width /
  // height (and position_x/y for W or N handles) plus the alignment
  // guide lines to render.
  const calculateResizeSnap = (fieldId, newX, newY, newWidth, newHeight, resizeDirection) => {
    if (!snapEnabled) {
      return { width: newWidth, height: newHeight, position_x: newX, position_y: newY, guides: { horizontal: [], vertical: [] } };
    }

    const otherFields = fields.filter(f => f.id !== fieldId && f.page === currentPage);
    const guides = { horizontal: [], vertical: [] };

    let width = newWidth;
    let height = newHeight;
    let x = newX;
    let y = newY;

    const dragRight = resizeDirection.includes('e');
    const dragLeft = resizeDirection.includes('w');
    const dragBottom = resizeDirection.includes('s');
    const dragTop = resizeDirection.includes('n');

    for (const other of otherFields) {
      const oLeft = other.position_x;
      const oRight = other.position_x + other.width;
      const oTop = other.position_y;
      const oBottom = other.position_y + other.height;

      // ── Horizontal snapping ──
      if (dragRight) {
        const right = x + width;
        // Snap right edge to other's right (aligns widths when left edges already match)
        if (Math.abs(right - oRight) < SNAP_THRESHOLD) {
          width = oRight - x;
          guides.vertical.push(oRight);
        } else if (Math.abs(right - oLeft) < SNAP_THRESHOLD) {
          width = oLeft - x;
          guides.vertical.push(oLeft);
        }
      }
      if (dragLeft) {
        const left = x;
        const anchorRight = newX + newWidth;  // right edge stays anchored as user drags W handle
        if (Math.abs(left - oLeft) < SNAP_THRESHOLD) {
          x = oLeft;
          width = anchorRight - x;
          guides.vertical.push(oLeft);
        } else if (Math.abs(left - oRight) < SNAP_THRESHOLD) {
          x = oRight;
          width = anchorRight - x;
          guides.vertical.push(oRight);
        }
      }

      // ── Vertical snapping ──
      if (dragBottom) {
        const bottom = y + height;
        if (Math.abs(bottom - oBottom) < SNAP_THRESHOLD) {
          height = oBottom - y;
          guides.horizontal.push(oBottom);
        } else if (Math.abs(bottom - oTop) < SNAP_THRESHOLD) {
          height = oTop - y;
          guides.horizontal.push(oTop);
        }
      }
      if (dragTop) {
        const top = y;
        const anchorBottom = newY + newHeight;
        if (Math.abs(top - oTop) < SNAP_THRESHOLD) {
          y = oTop;
          height = anchorBottom - y;
          guides.horizontal.push(oTop);
        } else if (Math.abs(top - oBottom) < SNAP_THRESHOLD) {
          y = oBottom;
          height = anchorBottom - y;
          guides.horizontal.push(oBottom);
        }
      }
    }

    // Fall back to grid snap on the dragged edge when no field alignment matched.
    if (gridSize > 1) {
      if (dragRight && guides.vertical.length === 0) {
        const right = Math.round((x + width) / gridSize) * gridSize;
        width = right - x;
      }
      if (dragLeft && guides.vertical.length === 0) {
        const anchorRight = newX + newWidth;
        x = Math.round(x / gridSize) * gridSize;
        width = anchorRight - x;
      }
      if (dragBottom && guides.horizontal.length === 0) {
        const bottom = Math.round((y + height) / gridSize) * gridSize;
        height = bottom - y;
      }
      if (dragTop && guides.horizontal.length === 0) {
        const anchorBottom = newY + newHeight;
        y = Math.round(y / gridSize) * gridSize;
        height = anchorBottom - y;
      }
    }

    // Enforce minimums (mirrors DraggableField clamps).
    width = Math.max(30, width);
    height = Math.max(15, height);

    return { width, height, position_x: x, position_y: y, guides };
  };

  // Update field with snapping
  const updateFieldWithSnap = (fieldId, updates) => {
    const field = fields.find(f => f.id === fieldId);
    if (!field) return;

    // Resize path — width/height changed (possibly with position_x/y if a
    // W/N handle is being dragged). Snap the edges that are MOVING under
    // the user's cursor to nearby other-field edges (so a resize naturally
    // lines up to siblings, mirroring the move-snap behaviour).
    const resizeDirection = updates._resizeDirection;
    if (resizeDirection && (updates.width !== undefined || updates.height !== undefined)) {
      const { width, height, position_x, position_y, guides } = calculateResizeSnap(
        fieldId,
        updates.position_x ?? field.position_x,
        updates.position_y ?? field.position_y,
        updates.width ?? field.width,
        updates.height ?? field.height,
        resizeDirection,
      );
      setSnapGuides(guides);
      const cleanUpdates = { ...updates, width, height, position_x, position_y };
      delete cleanUpdates._resizeDirection;
      updateField(fieldId, cleanUpdates);
      return;
    }

    if (updates.position_x !== undefined || updates.position_y !== undefined) {
      const newX = updates.position_x ?? field.position_x;
      const newY = updates.position_y ?? field.position_y;
      const { x, y, guides } = calculateSnap(fieldId, newX, newY, field.width, field.height);
      
      setSnapGuides(guides);
      updateField(fieldId, { ...updates, position_x: x, position_y: y });
    } else {
      updateField(fieldId, updates);
    }
  };
  
  // Clear snap guides when not dragging
  const clearSnapGuides = () => {
    setSnapGuides({ horizontal: [], vertical: [] });
  };

  const selectedFieldData = fields.find(f => f.id === selectedField);
  const currentPageFields = fields.filter(f => f.page === currentPage);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-forest" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="template-editor-page">
        {/* Header — wraps to two rows on narrow viewports so Save / Test
            Mapping are never hidden off-screen. */}
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(getTenantPath('/templates'))}
              data-testid="back-to-templates"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-3">
                {isRenaming ? (
                  <form
                    onSubmit={async (ev) => {
                      ev.preventDefault();
                      const next = renameValue.trim();
                      if (!next || next === template.name) {
                        setIsRenaming(false);
                        return;
                      }
                      try {
                        const fd = new FormData();
                        fd.append('name', next);
                        await templatesApi.update(templateId, fd);
                        setTemplate({ ...template, name: next });
                        toast.success('Template renamed');
                        setIsRenaming(false);
                      } catch (err) {
                        toast.error('Failed to rename template');
                      }
                    }}
                    className="flex items-center gap-2"
                  >
                    <Input
                      autoFocus
                      value={renameValue}
                      onChange={(ev) => setRenameValue(ev.target.value)}
                      onBlur={() => setIsRenaming(false)}
                      onKeyDown={(ev) => { if (ev.key === 'Escape') setIsRenaming(false); }}
                      className="h-9 font-heading text-2xl font-bold w-[340px]"
                      data-testid="template-name-input"
                    />
                    <Button type="submit" size="sm" data-testid="template-name-save" onMouseDown={(e) => e.preventDefault()}>Save</Button>
                  </form>
                ) : (
                  <button
                    type="button"
                    onClick={() => { setRenameValue(template?.name || ''); setIsRenaming(true); }}
                    className="group flex items-center gap-2 text-left"
                    data-testid="template-name-display"
                    title="Click to rename"
                  >
                    <h1 className="font-heading text-2xl font-bold text-foreground group-hover:underline decoration-dotted underline-offset-4">
                      {template?.name}
                    </h1>
                    <Pencil className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                )}
                {/* Entity Type Badge — fixed at creation, not editable here */}
                <div
                  className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1.5 ${ENTITY_COLORS[entityType]?.bg} ${ENTITY_COLORS[entityType]?.text}`}
                  data-testid="entity-type-badge"
                  title="Entity type is set when the template is created and can't be changed"
                >
                  {entityType === 'candidate' ? <User className="w-3 h-3" /> : <Building2 className="w-3 h-3" />}
                  {ENTITY_COLORS[entityType]?.label}
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Drag fields to position them on the document
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {/* Entity type is fixed when the template is created — the badge
                next to the template name shows it, read-only. It governs which
                Bullhorn fields the template can map, so changing it later would
                silently invalidate existing mappings. */}

            {/* Bullhorn File Type Selector */}
            <Select 
              value={selectedFileType || 'none'} 
              onValueChange={(value) => setSelectedFileType(value === 'none' ? null : value)}
            >
              <SelectTrigger className="w-[150px]" data-testid="file-type-select">
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-orange-500" />
                  <SelectValue placeholder="File Type..." />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <span className="text-gray-500">No File Type</span>
                </SelectItem>
                {loadingFileTypes ? (
                  <SelectItem value="loading" disabled>Loading...</SelectItem>
                ) : (
                  bullhornFileTypes.map((ft) => (
                    <SelectItem key={ft.value} value={ft.value}>
                      {ft.label}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            
            {/* Approval Toggle */}
            <Button
              variant={requiresApproval ? "default" : "outline"}
              size="sm"
              onClick={() => setRequiresApproval(!requiresApproval)}
              className={`gap-2 ${requiresApproval ? 'bg-amber-500 hover:bg-amber-600' : ''}`}
              data-testid="requires-approval-toggle"
              title={requiresApproval ? 'Approval required before Bullhorn sync' : 'Auto-sync to Bullhorn on completion'}
            >
              <ShieldCheck className="w-4 h-4" />
              {requiresApproval ? 'Approval' : 'No approval'}
            </Button>

            {/* Flow-back Toggle — whether the completed PDF uploads to the
                Bullhorn Files tab at all, or stays in HyreDocs only. */}
            <Button
              variant={bullhornFlowBack ? "default" : "outline"}
              size="sm"
              onClick={() => setBullhornFlowBack(!bullhornFlowBack)}
              className={`gap-2 ${bullhornFlowBack ? 'bg-blue-500 hover:bg-blue-600' : ''}`}
              data-testid="bullhorn-flow-back-toggle"
              title={bullhornFlowBack ? 'Completed documents upload to the Bullhorn Files tab' : 'Completed documents stay in HyreDocs only — nothing is written to Bullhorn Files'}
            >
              <Database className="w-4 h-4" />
              {bullhornFlowBack ? 'Flow back: On' : 'Flow back: Off'}
            </Button>

            {/* Visibility Toggle — Public/Private tag applied to the file when
                it's uploaded to Bullhorn. Meaningless (and disabled) when
                flow-back is off, since no file is ever uploaded. */}
            <Button
              variant={bullhornFileVisibility === 'private' ? "default" : "outline"}
              size="sm"
              onClick={() => setBullhornFileVisibility(bullhornFileVisibility === 'private' ? null : 'private')}
              className={`gap-2 ${bullhornFileVisibility === 'private' ? 'bg-purple-500 hover:bg-purple-600' : ''}`}
              disabled={!bullhornFlowBack}
              data-testid="bullhorn-visibility-toggle"
              title={!bullhornFlowBack
                ? 'Enable Flow back to control the uploaded file\'s Bullhorn visibility'
                : bullhornFileVisibility === 'private' ? 'Uploaded file is marked Private in Bullhorn' : 'Uploaded file uses Bullhorn\'s default visibility (Public)'}
            >
              {bullhornFileVisibility === 'private' ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
              {bullhornFileVisibility === 'private' ? 'Private' : 'Public'}
            </Button>

            {/* Document Settings (instructions + attachment-only mode) */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowSettingsPanel(true)}
              className="gap-2"
              data-testid="open-template-settings-button"
              title="Edit candidate instructions, attachment-only mode and required attachments"
            >
              <SettingsIcon className="w-4 h-4" />
              Settings
              {(candidateInstructions || templateMode === 'attachment_only') && (
                <span className="ml-1 inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              )}
            </Button>
            
            {/* PDF Upload */}
            <div className="relative">
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
                id="pdf-upload"
                data-testid="pdf-upload-input"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('pdf-upload').click()}
                disabled={uploading}
                data-testid="upload-pdf-button"
              >
                {uploading ? (
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                {pdfInfo.has_pdf ? 'Replace PDF' : 'Upload PDF'}
              </Button>
            </div>
            
            {/* AI Field Detection */}
            {pdfInfo.has_pdf && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAIDetection(true)}
                disabled={detecting}
                className="bg-gradient-to-r from-slate-50 to-orange-50 border-orange-200 hover:from-slate-100 hover:to-orange-100"
                data-testid="ai-detect-button"
              >
                {detecting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                    Detecting…
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                      <path d="M2 17l10 5 10-5"/>
                      <path d="M2 12l10 5 10-5"/>
                    </svg>
                    AI Detect
                  </>
                )}
              </Button>
            )}
            
            <Button
              variant="outline"
              size="sm"
              onClick={openTestMappingDialog}
              className="border-forest/30 text-forest hover:bg-forest/10"
              data-testid="test-mapping-button"
            >
              <Beaker className="w-4 h-4 mr-2" />
              Test
            </Button>

            {/* Bulk blend-into-document. Targets every locked
                (`candidate_editable === false`) field that hasn't
                already been blended. Idempotent — clicking again does
                nothing once everything is blended. */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const targets = fields.filter(
                  f => f.candidate_editable === false && !f.blend_into_document
                );
                if (targets.length === 0) {
                  toast.info('No locked fields left to blend.');
                  return;
                }
                setFields(prev => prev.map(f =>
                  (f.candidate_editable === false && !f.blend_into_document)
                    ? { ...f, blend_into_document: true }
                    : f
                ));
                toast.success(`Blended ${targets.length} locked field${targets.length === 1 ? '' : 's'} into the document`);
              }}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
              data-testid="blend-all-locked-button"
              title="Strip the amber outline + background from every locked field so values read as native body copy. Editable fields are untouched."
            >
              <Eye className="w-4 h-4 mr-2" />
              Blend locked
            </Button>

            <Button 
              size="sm"
              onClick={handleSave} 
              className="btn-primary"
              disabled={saving}
              data-testid="save-template-button"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  Saving…
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Editor Layout */}
        <div className="flex-1 grid grid-cols-12 gap-4 min-h-0">
          {/* Field Types Panel */}
          <div className="col-span-2 neumorphic rounded-xl p-3 overflow-auto">
            <h3 className="font-heading font-semibold text-sm mb-2 overline">Add Fields</h3>
            <p className="text-xs text-muted-foreground mb-3">Click or drag to canvas</p>
            <div className="space-y-1.5">
              {FIELD_TYPES.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => addField(value)}
                  draggable
                  onDragStart={(e) => handleFieldTypeDragStart(e, value)}
                  onDragEnd={() => setDraggedFieldType(null)}
                  className={`w-full flex items-center gap-2 p-2.5 rounded-lg transition-all duration-200 text-left text-sm border cursor-grab active:cursor-grabbing ${FIELD_COLORS[value]} active:scale-95 hover:shadow-md`}
                  data-testid={`add-${value}-field`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* PDF Canvas/Document Preview */}
          <div className="col-span-7 neumorphic rounded-xl p-4 overflow-hidden flex flex-col">
            <PdfEditorToolbar
              currentPage={currentPage}
              totalPages={pdfInfo.pages}
              onPageChange={setCurrentPage}
              scale={scale}
              onScaleChange={setScale}
              onFitToWidth={fitToWidth}
              snapEnabled={snapEnabled}
              onToggleSnap={() => setSnapEnabled(!snapEnabled)}
              onUndo={handleUndo}
              onRedo={handleRedo}
              canUndo={historyIndex > 0}
              canRedo={historyIndex < history.length - 1}
            />

            {/* PDF Viewer */}
            <div ref={pdfScrollRef} className="flex-1 overflow-auto bg-gray-100 rounded-lg">
              <div 
                ref={canvasRef}
                className={`relative bg-white mx-auto shadow-lg transition-all ${draggedFieldType ? 'ring-2 ring-[#f97316] ring-dashed' : ''}`}
                style={{ 
                  width: pdfInfo.width * scale, 
                  height: pdfInfo.height * scale,
                  minHeight: 600 * scale
                }}
                onClick={() => setSelectedField(null)}
                onDrop={handleCanvasDrop}
                onDragOver={handleCanvasDragOver}
              >
                {/* Drop indicator */}
                {draggedFieldType && (
                  <div className="absolute inset-0 bg-[#f97316]/5 pointer-events-none z-30 flex items-center justify-center">
                    <div className="bg-white/90 px-4 py-2 rounded-lg shadow-lg border border-[#f97316]">
                      <p className="text-sm font-medium text-[#f97316]">Drop to place field</p>
                    </div>
                  </div>
                )}
                
                {/* Snap alignment guides */}
                {snapEnabled && snapGuides.vertical.map((x, i) => (
                  <div 
                    key={`v-${i}`}
                    className="absolute top-0 bottom-0 w-px bg-primary/70 pointer-events-none z-50"
                    style={{ left: x * scale }}
                  />
                ))}
                {snapEnabled && snapGuides.horizontal.map((y, i) => (
                  <div 
                    key={`h-${i}`}
                    className="absolute left-0 right-0 h-px bg-primary/70 pointer-events-none z-50"
                    style={{ top: y * scale }}
                  />
                ))}
                
                {pdfInfo.has_pdf && template?.file_url ? (
                  <Document
                    file={`${BACKEND_URL}${template.file_url}${pdfCacheBust ? `${template.file_url.includes('?') ? '&' : '?'}cb=${pdfCacheBust}` : ''}`}
                    options={PDF_HTTP_OPTIONS}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={
                      <div className="flex items-center justify-center h-full">
                        <RefreshCw className="w-8 h-8 animate-spin text-[#f97316]" />
                      </div>
                    }
                    error={
                      <div className="flex items-center justify-center h-full text-muted-foreground">
                        <p>Failed to load PDF</p>
                      </div>
                    }
                  >
                    <Page 
                      pageNumber={currentPage} 
                      scale={scale}
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                    />
                  </Document>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-muted-foreground border-2 border-dashed border-border rounded-lg m-4">
                    <div className="text-center">
                      <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                      <p className="text-sm font-medium">No PDF uploaded</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Upload a PDF to position fields visually
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={() => document.getElementById('pdf-upload').click()}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload PDF
                      </Button>
                    </div>
                  </div>
                )}
                
                {/* Draggable Fields Overlay */}
                {currentPageFields.map((field) => (
                  <DraggableField
                    key={field.id}
                    field={field}
                    isSelected={selectedField === field.id}
                    scale={scale}
                    onSelect={setSelectedField}
                    onUpdate={updateField}
                    onDelete={deleteField}
                    onSnapUpdate={snapEnabled ? updateFieldWithSnap : null}
                    onDragEnd={clearSnapGuides}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Field Properties Panel */}
          <div className="col-span-3 neumorphic rounded-xl p-4 overflow-auto">
            {selectedFieldData ? (
              <div className="space-y-3" data-testid="field-properties">
                {/* Field Header with Type Badge */}
                <div className="flex items-center justify-between pb-3 border-b border-border">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${FIELD_COLORS[selectedFieldData.field_type]}`}>
                      {FIELD_TYPES.find(f => f.value === selectedFieldData.field_type)?.icon && 
                        (() => {
                          const Icon = FIELD_TYPES.find(f => f.value === selectedFieldData.field_type).icon;
                          return <Icon className="w-4 h-4" />;
                        })()
                      }
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Field Properties</p>
                      <p className="font-semibold text-sm capitalize">{selectedFieldData.field_type}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-500 hover:text-red-600 hover:bg-red-50 h-8 px-2"
                    onClick={() => {
                      deleteField(selectedField);
                      toast.success('Field deleted');
                    }}
                    data-testid="delete-field-button"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>

                {/* Label Input */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium">Label</Label>
                  <Input
                    value={selectedFieldData.label}
                    onChange={(e) => {
                      const nextLabel = e.target.value;
                      const updates = { label: nextLabel };
                      // Auto-infer currency for number fields — but ONLY
                      // when the recruiter hasn't already chosen one
                      // (and only on a fresh field, not when they're
                      // tweaking an existing labeled field's wording).
                      // We treat "user has chosen" as `currency_symbol`
                      // being a non-empty string OR being explicitly
                      // null (cleared via the Clear button). Undefined
                      // = never touched yet = safe to fill.
                      if (
                        selectedFieldData.field_type === 'number'
                        && selectedFieldData.currency_symbol === undefined
                      ) {
                        const inferred = inferCurrencyFromLabel(nextLabel, tenantDefaultCurrency);
                        if (inferred) {
                          updates.currency_symbol = inferred.symbol;
                          updates.currency_position = inferred.position;
                          updates.currency_format_money = inferred.formatMoney;
                        }
                      }
                      updateField(selectedField, updates);
                    }}
                    className="h-9 text-sm"
                    data-testid="field-label-input"
                  />
                </div>

                {/* Type & Page Row */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Type</Label>
                    <Select 
                      value={selectedFieldData.field_type}
                      onValueChange={(value) => updateField(selectedField, { field_type: value })}
                    >
                      <SelectTrigger className="h-9 text-sm" data-testid="field-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_TYPES.map(({ value, label, icon: Icon }) => (
                          <SelectItem key={value} value={value}>
                            <div className="flex items-center gap-2">
                              <Icon className="w-3.5 h-3.5" />
                              <span>{label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Page</Label>
                    <Select 
                      value={selectedFieldData.page?.toString() || '1'}
                      onValueChange={(value) => {
                        updateField(selectedField, { page: parseInt(value) });
                        setCurrentPage(parseInt(value));
                      }}
                    >
                      <SelectTrigger className="h-9 text-sm" data-testid="field-page-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: Math.max(pdfInfo.pages, 1) }, (_, i) => (
                          <SelectItem key={i + 1} value={(i + 1).toString()}>
                            Page {i + 1}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Required Toggle - Inline */}
                <div 
                  className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => updateField(selectedField, { required: !selectedFieldData.required })}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${selectedFieldData.required ? 'bg-red-500' : 'bg-gray-300'}`} />
                    <Label className="text-xs cursor-pointer">Required field</Label>
                  </div>
                  <Checkbox
                    checked={selectedFieldData.required}
                    onCheckedChange={(checked) => updateField(selectedField, { required: checked })}
                    data-testid="field-required-checkbox"
                  />
                </div>

                {/* Placeholder - Only for text-like fields */}
                {['text', 'email', 'phone', 'number'].includes(selectedFieldData.field_type) && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Placeholder</Label>
                    <Input
                      value={selectedFieldData.placeholder || ''}
                      onChange={(e) => updateField(selectedField, { placeholder: e.target.value })}
                      placeholder="e.g. Enter your name..."
                      className="h-9 text-sm"
                      data-testid="field-placeholder-input"
                    />
                  </div>
                )}

                {/* Text alignment — controls horizontal placement of the
                    rendered value inside the field bounding box on the
                    final PDF. Hidden for field types where alignment
                    doesn't apply (signature drawn as image, checkbox
                    as a tick mark, radio as a positioned dot). */}
                {!['signature', 'checkbox', 'radio_group'].includes(selectedFieldData.field_type) && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Text alignment</Label>
                    <div className="grid grid-cols-3 gap-1 p-0.5 bg-slate-100 rounded-md" data-testid="field-alignment-selector">
                      {[
                        { value: 'left',   label: 'Left',   icon: AlignLeft },
                        { value: 'center', label: 'Center', icon: AlignCenter },
                        { value: 'right',  label: 'Right',  icon: AlignRight },
                      ].map(opt => {
                        const Icon = opt.icon;
                        const active = (selectedFieldData.text_alignment || 'left') === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => updateField(selectedField, { text_alignment: opt.value })}
                            className={`text-xs py-1.5 rounded transition-colors inline-flex items-center justify-center gap-1 ${
                              active ? 'bg-white shadow-sm font-medium text-orange-600' : 'text-slate-600 hover:text-slate-900'
                            }`}
                            data-testid={`field-alignment-${opt.value}`}
                            title={`Align text ${opt.label.toLowerCase()}`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            <span className="hidden xl:inline">{opt.label}</span>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-muted-foreground -mt-0.5">
                      Where text sits inside the field box on the final PDF.
                    </p>
                  </div>
                )}

                {/* Vertical text alignment */}
                {!['signature', 'checkbox', 'radio_group'].includes(selectedFieldData.field_type) && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Vertical alignment</Label>
                    <div className="grid grid-cols-3 gap-1 p-0.5 bg-slate-100 rounded-md" data-testid="field-valign-selector">
                      {[
                        { value: 'top',    label: 'Top',    icon: ArrowUpToLine },
                        { value: 'middle', label: 'Middle', icon: AlignCenterVertical },
                        { value: 'bottom', label: 'Bottom', icon: ArrowDownToLine },
                      ].map(opt => {
                        const Icon = opt.icon;
                        const active = (selectedFieldData.text_vertical_alignment || 'top') === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => updateField(selectedField, { text_vertical_alignment: opt.value })}
                            className={`text-xs py-1.5 rounded transition-colors inline-flex items-center justify-center gap-1 ${
                              active ? 'bg-white shadow-sm font-medium text-orange-600' : 'text-slate-600 hover:text-slate-900'
                            }`}
                            data-testid={`field-valign-${opt.value}`}
                            title={`Align text ${opt.label.toLowerCase()}`}
                          >
                            <Icon className="w-3.5 h-3.5" />
                            <span className="hidden xl:inline">{opt.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Font size — the starting text size pdfTextLayout uses when
                    laying out the rendered value on the final PDF. Unset
                    (the default for every existing field) means "auto":
                    the renderer picks its own starting size and shrinks to
                    fit as needed — this control just overrides that starting
                    point, so a too-large choice can never overflow the box. */}
                {!['signature', 'checkbox', 'radio_group'].includes(selectedFieldData.field_type) && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Font size</Label>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => updateField(selectedField, {
                          font_size: Math.max(FONT_SIZE_MIN, (selectedFieldData.font_size ?? FONT_SIZE_DEFAULT) - 1),
                        })}
                        disabled={(selectedFieldData.font_size ?? FONT_SIZE_DEFAULT) <= FONT_SIZE_MIN}
                        className="h-8 w-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
                        data-testid="field-font-size-decrease"
                        title="Decrease font size"
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <div
                        className="flex-1 text-center text-sm font-medium text-slate-700"
                        data-testid="field-font-size-value"
                      >
                        {selectedFieldData.font_size ? `${selectedFieldData.font_size}pt` : 'Auto'}
                      </div>
                      <button
                        type="button"
                        onClick={() => updateField(selectedField, {
                          font_size: Math.min(FONT_SIZE_MAX, (selectedFieldData.font_size ?? FONT_SIZE_DEFAULT) + 1),
                        })}
                        disabled={(selectedFieldData.font_size ?? FONT_SIZE_DEFAULT) >= FONT_SIZE_MAX}
                        className="h-8 w-8 flex items-center justify-center rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
                        data-testid="field-font-size-increase"
                        title="Increase font size"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                      {selectedFieldData.font_size != null && (
                        <button
                          type="button"
                          onClick={() => updateField(selectedField, { font_size: undefined })}
                          className="text-[11px] text-slate-500 hover:text-slate-800 underline underline-offset-2"
                          data-testid="field-font-size-reset"
                        >
                          Reset
                        </button>
                      )}
                    </div>
                    <p className="text-[11px] text-muted-foreground -mt-0.5">
                      Starting text size on the final PDF — still shrinks automatically if the value doesn't fit the box.
                    </p>
                  </div>
                )}

                {/* Currency / unit — number fields only. Render with a
                    symbol (£, $, €, USD, /hr, %) before or after the
                    value on the PDF and the candidate input. Money
                    formatting (commas + 2dp) is on by default for
                    rate/wage use-cases and can be toggled off for
                    plain integer/count fields. */}
                {selectedFieldData.field_type === 'number' && (
                  <div className="space-y-2 py-2 px-3 rounded-lg bg-amber-50/60 border border-amber-100" data-testid="currency-section">
                    <Label className="text-xs font-medium flex items-center gap-1.5">
                      <span className="text-amber-700">Currency / unit</span>
                    </Label>
                    <p className="text-[10px] text-muted-foreground leading-snug">
                      Optional symbol shown alongside the number on the PDF and on the candidate&apos;s form.
                    </p>
                    <div className="flex gap-1.5">
                      {['£', '$', '€', '%'].map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => updateField(selectedField, {
                            currency_symbol: preset,
                            currency_position: 'prefix',
                            // Monetary presets should work immediately. A
                            // recruiter can still turn this off below for a
                            // non-money unit such as percentage.
                            currency_format_money: preset !== '%',
                          })}
                          className={`h-7 px-2 rounded-md text-xs font-semibold border transition-colors ${
                            selectedFieldData.currency_symbol === preset
                              ? 'bg-amber-500 border-amber-500 text-white'
                              : 'bg-white border-slate-200 hover:bg-amber-50'
                          }`}
                          data-testid={`currency-preset-${preset}`}
                        >
                          {preset}
                        </button>
                      ))}
                      {selectedFieldData.currency_symbol && (
                        <button
                          type="button"
                          onClick={() => updateField(selectedField, { currency_symbol: null })}
                          className="h-7 px-2 rounded-md text-xs text-slate-500 hover:text-red-600"
                          data-testid="currency-clear"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <Input
                      value={selectedFieldData.currency_symbol || ''}
                      onChange={(e) => updateField(selectedField, { currency_symbol: e.target.value || null })}
                      placeholder="Or type a custom symbol e.g. /hr, USD"
                      className="h-8 text-xs"
                      maxLength={6}
                      data-testid="currency-symbol-input"
                    />
                    {selectedFieldData.currency_symbol && (
                      <>
                        <Select
                          value={selectedFieldData.currency_position || 'prefix'}
                          onValueChange={(v) => updateField(selectedField, { currency_position: v })}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid="currency-position-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="prefix">Before value (e.g. £100)</SelectItem>
                            <SelectItem value="suffix">After value (e.g. 100%)</SelectItem>
                          </SelectContent>
                        </Select>
                        <div
                          className="flex items-center justify-between py-1.5 px-2 rounded-md bg-white border border-amber-100 cursor-pointer"
                          onClick={() => updateField(selectedField, { currency_format_money: !(selectedFieldData.currency_format_money !== false) })}
                        >
                          <Label className="text-[11px] cursor-pointer">Format as money (commas + 2dp)</Label>
                          <Checkbox
                            checked={selectedFieldData.currency_format_money !== false}
                            onCheckedChange={(checked) => updateField(selectedField, { currency_format_money: checked })}
                            data-testid="currency-format-money-checkbox"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* Dropdown Options */}
                {selectedFieldData.field_type === 'dropdown' && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-medium">Options</Label>
                    {selectedFieldData.bullhorn_picklist_options?.length > 0 ? (
                      // Bullhorn-managed picklist — labels are sourced
                      // from Bullhorn and editing them here would just
                      // de-sync the candidate UI from the actual write-
                      // back values. Render read-only.
                      <div
                        className="rounded-md border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs space-y-1 max-h-40 overflow-auto"
                        data-testid="bullhorn-picklist-readonly"
                      >
                        {selectedFieldData.bullhorn_picklist_options.map((opt) => (
                          <div key={opt.value} className="flex items-center justify-between gap-2">
                            <span className="text-emerald-900 font-medium truncate">{opt.label}</span>
                            <span className="text-emerald-700/70 text-[10px] font-mono shrink-0">{opt.value}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <Input
                        defaultValue={selectedFieldData.options?.join(', ') || ''}
                        key={selectedField}
                        onBlur={(e) => updateField(selectedField, {
                          options: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                        })}
                        placeholder="Option 1, Option 2, Option 3"
                        className="h-9 text-sm"
                        data-testid="dropdown-options-input"
                      />
                    )}
                    {selectedFieldData.bullhorn_picklist_options?.length > 0 ? (
                      <p className="text-xs text-emerald-700">
                        {selectedFieldData.bullhorn_picklist_options.length} option{selectedFieldData.bullhorn_picklist_options.length === 1 ? '' : 's'} synced from Bullhorn{selectedFieldData.bullhorn_picklist_entity_backed ? ' • candidates see labels, IDs are written back' : ''}
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground">Separate with commas, press Tab or click away to save</p>
                    )}
                  </div>
                )}

                {/* Radio Group Options */}
                {selectedFieldData.field_type === 'radio_group' && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium">Radio Options (select one)</Label>

                    {/* Layout selector */}
                    <div className="grid grid-cols-3 gap-1 p-0.5 bg-slate-100 rounded-md" data-testid="radio-layout-selector">
                      {[
                        { value: 'vertical', label: 'Vertical' },
                        { value: 'horizontal', label: 'Horizontal' },
                        { value: 'custom', label: 'Custom' },
                      ].map((mode) => (
                        <button
                          key={mode.value}
                          type="button"
                          onClick={() => updateField(selectedField, { options_layout: mode.value })}
                          className={`text-xs py-1 rounded transition-colors ${
                            (selectedFieldData.options_layout || 'vertical') === mode.value
                              ? 'bg-white shadow-sm font-medium text-orange-600'
                              : 'text-slate-600 hover:text-slate-900'
                          }`}
                          data-testid={`radio-layout-${mode.value}`}
                        >
                          {mode.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-muted-foreground -mt-1">
                      {selectedFieldData.options_layout === 'horizontal'
                        ? 'Options spread evenly across the field width.'
                        : selectedFieldData.options_layout === 'custom'
                        ? 'Drag each option on the canvas to position it exactly over the PDF radio circle. Make the field bounding box tall enough to span all options.'
                        : 'Options stacked vertically inside the field.'}
                    </p>

                    <div className="space-y-2">
                      {(selectedFieldData.options || []).map((opt, idx) => {
                        // Each option needs default toggle, label edit,
                        // delete, and (in custom layout) precise X/Y
                        // pixel positioning so recruiters can dial in
                        // a multi-circle PDF (e.g. Passport ☐ ☐ ☐) by
                        // typing exact coordinates rather than fiddling
                        // with the drag handles.
                        const isCustom = selectedFieldData.options_layout === 'custom';
                        const pos = (selectedFieldData.option_positions || {})[opt] || { x: 0, y: 0 };
                        const setPos = (axis, raw) => {
                          const n = Math.max(0, parseInt(raw, 10) || 0);
                          // Clamp into the field bounding box (leave 10px slack
                          // so the circle doesn't sit half-out the edge).
                          const max = axis === 'x'
                            ? Math.max(0, (selectedFieldData.width || 0) - 10)
                            : Math.max(0, (selectedFieldData.height || 0) - 10);
                          const clamped = Math.min(max, n);
                          const np = { ...(selectedFieldData.option_positions || {}) };
                          np[opt] = { ...pos, [axis]: clamped };
                          updateField(selectedField, { option_positions: np });
                        };
                        return (
                          <div key={`${selectedFieldData.id}-opt-${idx}`} className="space-y-1.5">
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  const next = String(selectedFieldData.default_value ?? '') === String(opt) ? '' : opt;
                                  updateField(selectedField, { default_value: next });
                                }}
                                title={String(selectedFieldData.default_value ?? '') === String(opt) ? 'Default value' : 'Set as default'}
                                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                  String(selectedFieldData.default_value ?? '') === String(opt)
                                    ? 'border-orange-500 bg-white'
                                    : 'border-slate-300 hover:border-orange-400'
                                }`}
                                data-testid={`radio-default-${idx}`}
                              >
                                {String(selectedFieldData.default_value ?? '') === String(opt) && (
                                  <span className="w-2 h-2 rounded-full bg-orange-500" />
                                )}
                              </button>
                              <Input
                                value={opt}
                                onChange={(e) => {
                                  const newOptions = [...(selectedFieldData.options || [])];
                                  newOptions[idx] = e.target.value;
                                  const patch = { options: newOptions };
                                  if (String(selectedFieldData.default_value ?? '') === String(opt)) {
                                    patch.default_value = e.target.value;
                                  }
                                  if (selectedFieldData.option_positions?.[opt]) {
                                    const np = { ...selectedFieldData.option_positions };
                                    np[e.target.value] = np[opt];
                                    delete np[opt];
                                    patch.option_positions = np;
                                  }
                                  updateField(selectedField, patch);
                                }}
                                className="h-8 text-sm flex-1"
                                placeholder={`Option ${idx + 1}`}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-500"
                                onClick={() => {
                                  const removed = selectedFieldData.options[idx];
                                  const newOptions = selectedFieldData.options.filter((_, i) => i !== idx);
                                  const patch = { options: newOptions };
                                  if (String(selectedFieldData.default_value ?? '') === String(removed)) {
                                    patch.default_value = '';
                                  }
                                  if (selectedFieldData.option_positions?.[removed]) {
                                    const np = { ...selectedFieldData.option_positions };
                                    delete np[removed];
                                    patch.option_positions = np;
                                  }
                                  updateField(selectedField, patch);
                                }}
                                disabled={(selectedFieldData.options || []).length <= 2}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>

                            {/* Per-option X/Y inputs — only in custom layout.
                                Mirrors the field's own position inputs so
                                recruiters can paste exact PDF coordinates. */}
                            {isCustom && (
                              <div className="flex items-center gap-1.5 pl-6 text-[11px] text-slate-600">
                                <span className="w-3 text-slate-400">X</span>
                                <Input
                                  type="number"
                                  value={pos.x}
                                  onChange={(e) => setPos('x', e.target.value)}
                                  className="h-7 text-xs px-1.5 w-16"
                                  data-testid={`radio-pos-x-${idx}`}
                                />
                                <span className="w-3 text-slate-400 ml-1">Y</span>
                                <Input
                                  type="number"
                                  value={pos.y}
                                  onChange={(e) => setPos('y', e.target.value)}
                                  className="h-7 text-xs px-1.5 w-16"
                                  data-testid={`radio-pos-y-${idx}`}
                                />
                                <span className="text-[10px] text-slate-400 ml-auto">px inside box</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newOptions = [...(selectedFieldData.options || []), `Option ${(selectedFieldData.options || []).length + 1}`];
                          updateField(selectedField, { options: newOptions });
                        }}
                        className="w-full h-8 text-xs"
                      >
                        + Add Option
                      </Button>

                      {/* Grid snap controls (only meaningful in custom layout) */}
                      {selectedFieldData.options_layout === 'custom' && (
                        <div className="pt-1 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <Label className="text-[11px] text-slate-600">Snap grid</Label>
                            <div className="flex gap-0.5 p-0.5 bg-slate-100 rounded-md" data-testid="radio-grid-selector">
                              {[
                                { value: 1, label: 'Off' },
                                { value: 5, label: '5px' },
                                { value: 10, label: '10px' },
                                { value: 20, label: '20px' },
                              ].map(g => {
                                const active = (selectedFieldData.option_grid_size ?? 5) === g.value;
                                return (
                                  <button
                                    key={g.value}
                                    type="button"
                                    onClick={() => updateField(selectedField, { option_grid_size: g.value })}
                                    className={`text-[10px] px-1.5 py-0.5 rounded transition-colors ${
                                      active ? 'bg-white shadow-sm font-medium text-orange-600' : 'text-slate-600 hover:text-slate-900'
                                    }`}
                                    data-testid={`radio-grid-${g.value}`}
                                  >
                                    {g.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <p className="text-[10px] text-slate-400 leading-snug">
                            Hold <kbd className="px-1 bg-slate-100 rounded text-[9px]">Shift</kbd> while dragging to ignore the grid for fine-tuning.
                          </p>
                          {/* Quick "snap into place" actions — distributes
                              every option evenly along the bounding box
                              and locks their cross-axis at the first
                              option's value. Solves the "I dragged three
                              radios but they're 25/37px apart instead of
                              evenly spaced" pain. */}
                          {(selectedFieldData.options || []).length >= 2 && (
                            <div className="flex gap-1 pt-1">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex-1 h-7 text-[11px]"
                                data-testid="radio-distribute-vertical"
                                onClick={() => {
                                  const opts = selectedFieldData.options || [];
                                  if (opts.length < 2) return;
                                  const h = Math.max(20, selectedFieldData.height || 24);
                                  const w = Math.max(20, selectedFieldData.width || 24);
                                  // Snap to grid for tidy values.
                                  const grid = Math.max(1, selectedFieldData.option_grid_size ?? 5);
                                  const positions = { ...(selectedFieldData.option_positions || {}) };
                                  // Use the first option's X as the shared
                                  // column (so all dots vertically align).
                                  const sharedX = positions[opts[0]]?.x ?? 0;
                                  const innerH = Math.max(0, h - 10);
                                  opts.forEach((opt, i) => {
                                    const rawY = (innerH * i) / Math.max(1, opts.length - 1);
                                    const y = Math.min(innerH, Math.round(rawY / grid) * grid);
                                    const x = Math.min(Math.max(0, w - 10), sharedX);
                                    positions[opt] = { x, y };
                                  });
                                  updateField(selectedField, { option_positions: positions });
                                }}
                              >
                                Distribute Y
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="flex-1 h-7 text-[11px]"
                                data-testid="radio-distribute-horizontal"
                                onClick={() => {
                                  const opts = selectedFieldData.options || [];
                                  if (opts.length < 2) return;
                                  const h = Math.max(20, selectedFieldData.height || 24);
                                  const w = Math.max(20, selectedFieldData.width || 24);
                                  const grid = Math.max(1, selectedFieldData.option_grid_size ?? 5);
                                  const positions = { ...(selectedFieldData.option_positions || {}) };
                                  const sharedY = positions[opts[0]]?.y ?? 0;
                                  const innerW = Math.max(0, w - 10);
                                  opts.forEach((opt, i) => {
                                    const rawX = (innerW * i) / Math.max(1, opts.length - 1);
                                    const x = Math.min(innerW, Math.round(rawX / grid) * grid);
                                    const y = Math.min(Math.max(0, h - 10), sharedY);
                                    positions[opt] = { x, y };
                                  });
                                  updateField(selectedField, { option_positions: positions });
                                }}
                              >
                                Distribute X
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {selectedFieldData.options_layout === 'custom'
                        && Object.keys(selectedFieldData.option_positions || {}).length > 0 && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateField(selectedField, { option_positions: {} })}
                            className="w-full h-7 text-xs text-muted-foreground"
                            data-testid="reset-radio-positions"
                          >
                            Reset custom positions
                          </Button>
                        )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Click an option's circle to mark it as the default. The candidate sees it pre-selected.
                      {selectedFieldData.default_value && (
                        <> Currently pre-selected: <strong className="text-orange-600">{selectedFieldData.default_value}</strong>.</>
                      )}
                    </p>

                    {/* Overlay-on-PDF mode — when the recruiter is placing
                        radio dots directly over an existing "Yes / No"
                        already printed on the base PDF, both toggles
                        together remove the field's chrome AND hide the
                        option labels so only the selectable dots show. */}
                    <div className="space-y-2 pt-3 mt-2 border-t border-dashed border-slate-200">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">PDF overlay mode</p>
                      <div
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                        onClick={() => updateField(selectedField, { transparent_background: !selectedFieldData.transparent_background })}
                      >
                        <div className="min-w-0 pr-2">
                          <Label className="text-xs cursor-pointer block">Transparent background</Label>
                          <p className="text-[10px] text-muted-foreground leading-snug">Hide the colored box so the PDF text underneath shows through.</p>
                        </div>
                        <Checkbox
                          checked={!!selectedFieldData.transparent_background}
                          onCheckedChange={(checked) => updateField(selectedField, { transparent_background: checked })}
                          data-testid="transparent-background-checkbox"
                        />
                      </div>
                      <div
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                        onClick={() => updateField(selectedField, { hide_option_labels: !selectedFieldData.hide_option_labels })}
                      >
                        <div className="min-w-0 pr-2">
                          <Label className="text-xs cursor-pointer block">Hide option labels</Label>
                          <p className="text-[10px] text-muted-foreground leading-snug">Show only the radio dot — useful when "Yes" / "No" is already printed on the PDF. Labels are kept for conditional logic.</p>
                        </div>
                        <Checkbox
                          checked={!!selectedFieldData.hide_option_labels}
                          onCheckedChange={(checked) => updateField(selectedField, { hide_option_labels: checked })}
                          data-testid="hide-option-labels-checkbox"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Bullhorn Integration Section */}
                <div className="pt-3 mt-2 border-t border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <Database className="w-4 h-4 text-[#4CAF50]" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Bullhorn</span>
                    {loadingFields && <RefreshCw className="w-3 h-3 animate-spin" />}
                  </div>

                  {/* Scope banner — makes it visually obvious which entity's
                      Bullhorn fields the recruiter is mapping against. For
                      placement templates the scope tabs below let them pick
                      between Candidate / JobOrder / ClientCorporation; for
                      candidate/contact templates only that entity's fields
                      are available by design (matches the per-party prefill
                      logic when a packet is sent at placement level). */}
                  <div className={`text-[11px] rounded-md px-2.5 py-1.5 mb-3 border ${
                    entityType === 'placement'
                      ? 'bg-[#1a4a5e]/5 border-[#1a4a5e]/20 text-[#1a4a5e]'
                      : entityType === 'contact'
                        ? 'bg-amber-50 border-amber-200 text-amber-900'
                        : 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  }`} data-testid="mapping-scope-banner">
                    {entityType === 'placement' && (
                      <>Mapping <strong>placement</strong> fields. Sends from a placement record auto-route candidate documents to the candidate and contact documents to the client contact.</>
                    )}
                    {entityType === 'candidate' && (
                      <>Mapping <strong>candidate</strong> fields only. To map placement, job-order or company fields, change Entity type to <strong>Placement</strong>.</>
                    )}
                    {entityType === 'contact' && (
                      <>Mapping <strong>client contact</strong> fields only. To map placement or company fields, change Entity type to <strong>Placement</strong>.</>
                    )}
                  </div>
                  
                  {/* Source Field from Bullhorn - with Search and Frequently Used section */}
                  <div className="space-y-1.5 mb-3">
                    <Label className="text-xs font-medium flex items-center gap-1.5">
                      Map to Field
                      <span className="text-muted-foreground font-normal">({getBullhornFields().totalCount || bullhornFields.length || 'loading...'})</span>
                    </Label>
                    {['signature', 'file_upload'].includes(selectedFieldData.field_type) ? (
                      <p className="text-[11px] text-muted-foreground">
                        {selectedFieldData.field_type === 'signature' ? 'Signature' : 'File-upload'} fields can't be pre-filled from or written back to a Bullhorn field.
                      </p>
                    ) : FIELD_TYPE_BULLHORN_COMPAT[selectedFieldData.field_type] ? (
                      <p className="text-[11px] text-muted-foreground">
                        Only showing Bullhorn fields a <strong>{FIELD_TYPES.find(f => f.value === selectedFieldData.field_type)?.label || selectedFieldData.field_type}</strong> field can hold.
                      </p>
                    ) : null}

                    {/* Entity scope selector — OUTSIDE SelectContent to avoid Radix focus bugs */}
                    {bullhornFields.some(f => f.entityGroup) && (
                      <div className="flex flex-wrap items-center gap-1 mb-1.5" data-testid="mapping-scope-tabs">
                        {['all', 'Placement', 'Candidate', 'Job Order', 'Client Company'].map(scope => {
                          const icons = { 'all': '◎', 'Placement': '📄', 'Candidate': '👤', 'Job Order': '💼', 'Client Company': '🏢' };
                          const count = scope === 'all'
                            ? bullhornFields.length
                            : bullhornFields.filter(f => (f.entityGroup || 'Placement') === scope).length;
                          return (
                            <button
                              key={scope}
                              type="button"
                              onClick={() => setMappingScope(scope)}
                              className={`text-xs px-2 py-1 rounded flex items-center gap-1 transition ${
                                mappingScope === scope
                                  ? 'bg-forest text-white'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                              data-testid={`scope-${scope.replace(/ /g, '-').toLowerCase()}`}
                            >
                              <span>{icons[scope]}</span>
                              <span>{scope === 'all' ? 'All' : scope}</span>
                              <span className="opacity-70">{count}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    
                    <Select 
                      value={selectedFieldData.bullhorn_source_field || selectedFieldData.mapped_bullhorn_field || 'none'}
                      onValueChange={(value) => {
                        const cleared = value === 'none';
                        const isReference = value === '__candidate_reference__';
                        const isSkill = ['skills', 'primarySkills', 'secondarySkills'].includes(value);
                        const isCategory = ['category', 'categories'].includes(value);
                        // Composite (address) leaves are always read-only.
                        const mappedIsComposite = !cleared && bullhornFieldIsComposite(
                          (bullhornFields || []).find(f => f.value === value) || { value }
                        );

                        // Auto-cascade wiring (Option A): Skill fields should
                        // filter by Category by default. We compute both
                        // directions here in one pass so we never store an
                        // inconsistent state.
                        //   • If the new mapping is a Skill and a Category
                        //     field already exists on this template → set this
                        //     field's filter_by_field_id to that Category's id.
                        //   • If the new mapping is a Category and any Skill
                        //     field on this template is currently unlinked →
                        //     queue patches against those skill fields below.
                        const otherFields = (fields || []).filter(f => f.id !== selectedField);
                        const categoryField = otherFields.find(f => {
                          const m = f.bullhorn_source_field || f.mapped_bullhorn_field || '';
                          return ['category', 'categories'].includes(m);
                        });
                        const orphanSkillFields = isCategory
                          ? otherFields.filter(f => {
                              const m = f.bullhorn_source_field || f.mapped_bullhorn_field || '';
                              return ['skills', 'primarySkills', 'secondarySkills'].includes(m)
                                && !f.filter_by_field_id;
                            })
                          : [];

                        updateField(selectedField, {
                          bullhorn_source_field: cleared ? '' : value,
                          mapped_bullhorn_field: cleared ? '' : value,
                          // If the previous mapping had baked-in Bullhorn
                          // picklist options, drop them when the mapping
                          // is cleared so a different field's options
                          // don't linger on screen.
                          ...(cleared ? { options: [] } : {}),
                          // Reference mapping is a virtual sentinel — auto-seed
                          // the reference_index/role so the purple block lights up
                          // immediately. Switching to a normal field clears them
                          // so we don't accidentally double-post on completion.
                          ...(isReference
                            ? {
                                reference_index: selectedFieldData.reference_index || 1,
                                reference_role: selectedFieldData.reference_role || 'name',
                              }
                            : { reference_index: null, reference_role: null }),
                          // Auto-link Skill → Category when both exist on this template.
                          // Skipped when the recruiter previously cleared the link.
                          ...(isSkill && categoryField && selectedFieldData.filter_by_field_id !== ''
                            ? { filter_by_field_id: categoryField.id }
                            : {}),
                          // If the field is no longer a Skill mapping, drop the cascading link.
                          ...(!isSkill && selectedFieldData.filter_by_field_id
                            ? { filter_by_field_id: '' }
                            : {}),
                          // A composite (address) leaf can only be pre-filled, never
                          // candidate-edited — Bullhorn merges the whole address back.
                          ...(mappedIsComposite ? { candidate_editable: false } : {}),
                        });

                        // Now wire up any orphan Skill fields to point at this
                        // newly-mapped Category. This is what makes "add a
                        // Category field after a Skill field" still cascade.
                        if (orphanSkillFields.length > 0) {
                          orphanSkillFields.forEach((sf) => {
                            updateField(sf.id, { filter_by_field_id: selectedField });
                          });
                          toast.message(
                            orphanSkillFields.length === 1
                              ? 'Linked Skill field to filter by this Category'
                              : `Linked ${orphanSkillFields.length} Skill fields to filter by this Category`,
                          );
                        } else if (isSkill && categoryField && !selectedFieldData.filter_by_field_id) {
                          toast.message('Skill options will filter by the candidate\'s Category selection');
                        }

                        setFieldSearchQuery('');

                        // Auto-pull picklist options from Bullhorn for the
                        // newly-mapped field. Updates `field.options` so the
                        // candidate (and the recruiter previewing) sees a
                        // dropdown with the same values Bullhorn accepts.
                        if (!cleared && !isReference) {
                          const targetEntity = entityType || 'candidate';
                          axios.get(
                            `${API_URL}/bullhorn/field-options`,
                            { params: { entity: targetEntity, field: value } },
                          ).then(({ data }) => {
                            if (data?.is_picklist && Array.isArray(data.options) && data.options.length) {
                              const optionValues = data.options
                                .map(o => o.value)
                                .filter(Boolean);
                              const patch = {
                                options: optionValues,
                                bullhorn_picklist_options: data.options,
                                bullhorn_picklist_multi: !!data.multi,
                                bullhorn_picklist_entity_backed: !!data.entity_backed,
                              };
                              // Force the field type to dropdown so the
                              // candidate sees a real dropdown, not a
                              // free-text input. (multi-select picklists
                              // surface in candidate UI as multiple-choice.)
                              const isTextLike = !selectedFieldData.field_type
                                || ['text', 'textarea', 'email', 'phone', 'number'].includes(selectedFieldData.field_type);
                              if (isTextLike) {
                                patch.field_type = data.multi ? 'checkbox_group' : 'dropdown';
                              }
                              updateField(selectedField, patch);
                              toast.success(`Pulled ${optionValues.length} option${optionValues.length === 1 ? '' : 's'} from Bullhorn`);
                            }
                          }).catch(() => {
                            // non-fatal — recruiter can still type options manually
                          });
                        }
                      }}
                      disabled={loadingFields}
                    >
                      <SelectTrigger className="h-9 text-sm" data-testid="bullhorn-source-select">
                        <SelectValue placeholder={loadingFields ? "Loading..." : "Select field"} />
                      </SelectTrigger>
                      <SelectContent className="max-h-96">
                        {/* Search Input */}
                        <div className="px-2 pb-2 pt-2 sticky top-0 bg-popover z-10">
                          <div className="relative">
                            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground z-10 pointer-events-none" />
                            <SelectSearchInput
                              value={fieldSearchQuery}
                              onChange={(e) => setFieldSearchQuery(e.target.value)}
                              placeholder="Search fields..."
                              testId="bullhorn-field-search-input"
                              className="w-full h-8 pl-8 pr-3 text-sm border rounded-md bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          </div>
                        </div>
                        
                        <SelectItem value="none">
                          <span className="text-muted-foreground">No mapping</span>
                        </SelectItem>

                        {/* Special: virtual Candidate Reference mapping.
                            Picking this tells HyreDocs the field is part of a
                            CandidateReference record (not a normal write-back
                            target). Only meaningful on candidate templates;
                            selection auto-seeds reference_index=1, role=name. */}
                        {entityType === 'candidate' && (
                          <SelectItem value="__candidate_reference__" data-testid="map-to-candidate-reference">
                            <div className="flex items-center gap-2">
                              <Users className="w-3.5 h-3.5 text-purple-600" />
                              <span className="text-purple-700 font-medium">Candidate Reference</span>
                              <span className="text-xs text-muted-foreground ml-auto">references</span>
                            </div>
                          </SelectItem>
                        )}
                        
                        {/* Show search results or categorized fields */}
                        {fieldSearchQuery ? (
                          // Search results
                          <>
                            {getBullhornFields(fieldSearchQuery).frequent.length > 0 || 
                             getBullhornFields(fieldSearchQuery).others.length > 0 ? (
                              <>
                                {getBullhornFields(fieldSearchQuery).frequent.map(({ value, label }) => (
                                  <SelectItem key={value} value={value}>
                                    <div className="flex items-center gap-2">
                                      <Star className="w-3 h-3 text-amber-500" />
                                      <span>{label}</span>
                                      <span className="text-xs text-muted-foreground ml-auto">{value}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                                {getBullhornFields(fieldSearchQuery).others.map(({ value, label }) => (
                                  <SelectItem key={value} value={value}>
                                    <div className="flex items-center justify-between w-full">
                                      <span>{label}</span>
                                      <span className="text-xs text-muted-foreground">{value}</span>
                                    </div>
                                  </SelectItem>
                                ))}
                              </>
                            ) : (
                              <div className="px-2 py-4 text-sm text-muted-foreground text-center">
                                No fields match "{fieldSearchQuery}"
                              </div>
                            )}
                          </>
                        ) : (
                          // Default categorized view
                          <>
                            {/* Frequently Used Section */}
                            <SelectGroup>
                              <SelectLabel className="flex items-center gap-1.5 text-xs">
                                <Star className="w-3 h-3 text-amber-500" />
                                Frequently Used
                              </SelectLabel>
                              {getBullhornFields().frequent.map(({ value, label }) => (
                                <SelectItem key={value} value={value}>
                                  {label}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                            
                            {/* All Other Fields — grouped by sub-entity for placements */}
                            {(() => {
                              const othersList = getBullhornFields().others;
                              const hasGroups = othersList.some(f => f.entityGroup);
                              
                              if (!hasGroups) {
                                return (
                                  <SelectGroup>
                                    <SelectLabel className="text-xs">All Fields</SelectLabel>
                                    {othersList.map(({ value, label, disabled }) => (
                                      <SelectItem key={value} value={value} disabled={disabled}>
                                        <div className="flex items-center justify-between w-full">
                                          <span>{label}</span>
                                          {value !== label && (
                                            <span className="text-xs text-muted-foreground ml-2">{value}</span>
                                          )}
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </SelectGroup>
                                );
                              }
                              
                              // Group by entityGroup (Placement | Candidate | Job Order | Client Company)
                              const groupOrder = ['Placement', 'Candidate', 'Job Order', 'Client Company', 'Other'];
                              const groupIcons = {
                                'Placement': '📄',
                                'Candidate': '👤',
                                'Job Order': '💼',
                                'Client Company': '🏢',
                                'Other': '—',
                              };
                              const grouped = {};
                              othersList.forEach(f => {
                                const g = f.entityGroup || 'Other';
                                if (!grouped[g]) grouped[g] = [];
                                grouped[g].push(f);
                              });
                              
                              return groupOrder.filter(g => grouped[g]?.length).map(g => (
                                <SelectGroup key={g}>
                                  <SelectLabel className="text-xs flex items-center gap-1.5 mt-1 sticky top-8 bg-popover">
                                    <span>{groupIcons[g]}</span>
                                    <span className="font-semibold">{g}</span>
                                    <span className="text-muted-foreground font-normal">({grouped[g].length})</span>
                                  </SelectLabel>
                                  {grouped[g].map(({ value, label, disabled }) => (
                                    <SelectItem key={value} value={value} disabled={disabled}>
                                      <div className="flex items-center justify-between w-full">
                                        <span>{label}</span>
                                        {value !== label && (
                                          <span className="text-xs text-muted-foreground ml-2 font-mono">{value}</span>
                                        )}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectGroup>
                              ));
                            })()}
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    
                    {/* Write-back destination indicator */}
                    {(selectedFieldData.bullhorn_source_field || selectedFieldData.mapped_bullhorn_field) && (() => {
                      const mapped = selectedFieldData.bullhorn_source_field || selectedFieldData.mapped_bullhorn_field;
                      let dest = null;
                      let icon = '📄';
                      if (mapped.startsWith('candidate.')) { dest = 'Candidate record'; icon = '👤'; }
                      else if (mapped.startsWith('jobOrder.')) { dest = 'Job Order record'; icon = '💼'; }
                      else if (mapped.startsWith('clientCorporation.') || mapped.startsWith('company.')) { dest = 'Client Company record'; icon = '🏢'; }
                      else if (mapped.startsWith('clientContact.') || mapped.startsWith('contact.')) { dest = 'Client Contact record'; icon = '👥'; }
                      else {
                        if (entityType === 'placement') { dest = 'Placement record'; icon = '📄'; }
                        else if (entityType === 'contact') { dest = 'Client Contact record'; icon = '👥'; }
                        else { dest = 'Candidate record'; icon = '👤'; }
                      }
                      return (
                        <div className="mt-2 flex items-start gap-2 px-2.5 py-1.5 rounded-md bg-blue-50 border border-blue-100 text-xs" data-testid="writeback-destination-indicator">
                          <span className="text-sm leading-none mt-0.5">{icon}</span>
                          <div className="flex-1">
                            <span className="text-blue-900 font-medium">Writes back to:</span>{' '}
                            <span className="text-blue-700">{dest}</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Cascading filter — only relevant for Skills mappings.
                        Auto-linked by default when a Category-mapped field
                        exists; recruiter can override here. */}
                    {(() => {
                      const mapped = selectedFieldData.bullhorn_source_field || selectedFieldData.mapped_bullhorn_field || '';
                      const isSkillField = ['skills', 'primarySkills', 'secondarySkills'].includes(mapped);
                      if (!isSkillField) return null;
                      const categoryFields = (fields || []).filter((f) => {
                        if (f.id === selectedField) return false;
                        const m = f.bullhorn_source_field || f.mapped_bullhorn_field || '';
                        return ['category', 'categories'].includes(m);
                      });
                      const isAutoLinked = !!selectedFieldData.filter_by_field_id;
                      return (
                        <div className="mt-3 space-y-1.5" data-testid="filter-by-field-block">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs font-medium flex items-center gap-1.5">
                              <Sparkles className="w-3 h-3 text-orange-500" />
                              Filter skill options by category
                            </Label>
                            {isAutoLinked && (
                              <span className="text-[10px] uppercase tracking-wide font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                                Auto-linked
                              </span>
                            )}
                          </div>
                          <Select
                            value={selectedFieldData.filter_by_field_id || 'none'}
                            onValueChange={(v) => updateField(selectedField, { filter_by_field_id: v === 'none' ? '' : v })}
                          >
                            <SelectTrigger className="h-8 text-sm" data-testid="filter-by-field-select">
                              <SelectValue placeholder="No filter (show all skills)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">
                                <span className="text-muted-foreground">No filter (show all skills)</span>
                              </SelectItem>
                              {categoryFields.length === 0 ? (
                                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                                  Map another field on this template to <code>category</code> first.
                                </div>
                              ) : (
                                categoryFields.map((cf) => (
                                  <SelectItem key={cf.id} value={cf.id}>
                                    {cf.label} <span className="text-muted-foreground text-xs ml-2">({cf.bullhorn_source_field || cf.mapped_bullhorn_field})</span>
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                          {isAutoLinked ? (
                            <p className="text-xs text-emerald-700">
                              When the candidate picks a category, only matching skills will be shown. Set to "No filter" to show every skill instead.
                            </p>
                          ) : (
                            <p className="text-xs text-muted-foreground">
                              Showing the full Bullhorn skill list. Pick a Category field above to cascade.
                            </p>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Compact Toggle Row */}
                  <div className="space-y-2">
                    {/* Requester/Admin field — a field the SENDER fills in (or
                        signs) while sending, never shown to the candidate at
                        all. Distinct from "Candidate can edit": that just
                        locks a field's value (e.g. an auto-computed date);
                        this one makes the sender's input mandatory before the
                        document can even be sent. Forces candidate_editable
                        off below since the two can't both be true. */}
                    <div
                      className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => updateField(selectedField, {
                        sender_fillable: !selectedFieldData.sender_fillable,
                        ...(!selectedFieldData.sender_fillable ? { candidate_editable: false } : {}),
                      })}
                    >
                      <div className="flex items-center gap-2">
                        <ShieldCheck className={`w-3.5 h-3.5 ${selectedFieldData.sender_fillable ? 'text-violet-600' : 'text-gray-400'}`} />
                        <Label className="text-xs cursor-pointer">Requester/Admin field</Label>
                      </div>
                      <Checkbox
                        checked={!!selectedFieldData.sender_fillable}
                        onCheckedChange={(checked) => updateField(selectedField, {
                          sender_fillable: checked,
                          ...(checked ? { candidate_editable: false } : {}),
                        })}
                        data-testid="sender-fillable-checkbox"
                      />
                    </div>
                    {selectedFieldData.sender_fillable && (
                      <p className="text-xs text-violet-700 bg-violet-50 px-3 py-2 rounded-lg">
                        Not shown to the {entityType === 'contact' ? 'contact' : 'candidate'} — the sender must
                        {selectedFieldData.field_type === 'signature' ? ' sign' : ' fill this in'} before the
                        document can be sent.
                      </p>
                    )}

                    {/* Candidate Can Edit — forced off for composite (address)
                        mappings, which Bullhorn only accepts as a whole. */}
                    {(() => {
                      const activeMapping = selectedFieldData.bullhorn_source_field || selectedFieldData.mapped_bullhorn_field;
                      const activeOpt = (bullhornFields || []).find(f => f.value === activeMapping) || { value: activeMapping };
                      const mappingIsComposite = bullhornFieldIsComposite(activeOpt);
                      const compositeParts = activeOpt.composite_parts || [];
                      const locked = selectedFieldData.sender_fillable || mappingIsComposite;
                      return (
                        <>
                          <div
                            className={`flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 transition-colors ${locked ? 'opacity-50 pointer-events-none' : 'cursor-pointer hover:bg-muted/50'}`}
                            onClick={() => !locked && updateField(selectedField, { candidate_editable: selectedFieldData.candidate_editable === false })}
                          >
                            <div className="flex items-center gap-2">
                              {(!locked && selectedFieldData.candidate_editable !== false) ? (
                                <Unlock className="w-3.5 h-3.5 text-green-600" />
                              ) : (
                                <Lock className="w-3.5 h-3.5 text-amber-600" />
                              )}
                              <Label className="text-xs cursor-pointer">
                                {entityType === 'contact' ? 'Contact' : 'Candidate'} can edit
                              </Label>
                            </div>
                            <Checkbox
                              checked={!locked && selectedFieldData.candidate_editable !== false}
                              disabled={locked}
                              onCheckedChange={(checked) => updateField(selectedField, { candidate_editable: checked })}
                              data-testid="candidate-editable-checkbox"
                            />
                          </div>
                          {mappingIsComposite && !selectedFieldData.sender_fillable && (
                            <p className="text-[11px] text-muted-foreground px-3">
                              To create candidate editable fields, use the individual parts of this composite field
                              {compositeParts.length > 0 && <>: {compositeParts.join(', ')}</>}.
                            </p>
                          )}
                        </>
                      );
                    })()}

                    {/* Postal Address Format — only meaningful for a
                        whole-address composite mapping (candidate.address,
                        contact.address, company.address, etc.). Renders each
                        address part on its own line, letterhead-style,
                        skipping any that are blank, with the country
                        appended — instead of the default single
                        comma-joined line. */}
                    {(() => {
                      const activeMapping = selectedFieldData.bullhorn_source_field || selectedFieldData.mapped_bullhorn_field;
                      const activeOpt = (bullhornFields || []).find(f => f.value === activeMapping) || { value: activeMapping };
                      if (!bullhornFieldIsComposite(activeOpt)) return null;
                      return (
                        <>
                          <div
                            className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                            onClick={() => updateField(selectedField, { postal_address_format: !selectedFieldData.postal_address_format })}
                          >
                            <div className="flex items-center gap-2">
                              <MapPin className={`w-3.5 h-3.5 ${selectedFieldData.postal_address_format ? 'text-blue-600' : 'text-gray-400'}`} />
                              <Label className="text-xs cursor-pointer">Postal Address Format</Label>
                            </div>
                            <Checkbox
                              checked={!!selectedFieldData.postal_address_format}
                              onCheckedChange={(checked) => updateField(selectedField, { postal_address_format: checked })}
                              data-testid="postal-address-format-checkbox"
                            />
                          </div>
                          {selectedFieldData.postal_address_format && (
                            <>
                              <p className="text-[11px] text-muted-foreground px-3">
                                Each address line is shown on its own line (blank lines, like a missing county, are
                                skipped) — like an address on a letterhead.
                              </p>
                              <div
                                className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                                onClick={() => updateField(selectedField, { postal_address_include_country: selectedFieldData.postal_address_include_country === false })}
                              >
                                <Label className="text-xs cursor-pointer pl-5">Include Country</Label>
                                <Checkbox
                                  checked={selectedFieldData.postal_address_include_country !== false}
                                  onCheckedChange={(checked) => updateField(selectedField, { postal_address_include_country: checked })}
                                  data-testid="postal-address-include-country-checkbox"
                                />
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}

                    {/* Always show when empty — only meaningful for locked
                        (non-editable) fields. By default a locked field that
                        has NO Bullhorn value is hidden from the candidate's
                        view so an empty "New Number" placeholder doesn't
                        clutter the form. Recruiters who want the empty box
                        to remain visible (e.g. "Will be provided later" copy)
                        can enable this toggle. Auto-hidden when the field
                        is editable since editable + empty is always shown. */}
                    {selectedFieldData.candidate_editable === false && (
                      <div
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => updateField(selectedField, { always_show_when_empty: !selectedFieldData.always_show_when_empty })}
                      >
                        <div className="flex items-center gap-2">
                          <Eye className={`w-3.5 h-3.5 ${selectedFieldData.always_show_when_empty ? 'text-blue-600' : 'text-gray-400'}`} />
                          <Label className="text-xs cursor-pointer">Show even when empty</Label>
                        </div>
                        <Checkbox
                          checked={selectedFieldData.always_show_when_empty || false}
                          onCheckedChange={(checked) => updateField(selectedField, { always_show_when_empty: checked })}
                          data-testid="always-show-when-empty-checkbox"
                        />
                      </div>
                    )}

                    {/* Blend into document — strips the locked-field amber
                        background/border so the prefilled value reads as
                        native body copy. Only meaningful when the field is
                        non-editable (editable fields keep the blue outline
                        to signal "you can change this"). */}
                    {selectedFieldData.candidate_editable === false && (
                      <div
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => updateField(selectedField, { blend_into_document: !selectedFieldData.blend_into_document })}
                        data-testid="blend-into-document-toggle"
                      >
                        <div className="flex items-center gap-2">
                          <Eye className={`w-3.5 h-3.5 ${selectedFieldData.blend_into_document ? 'text-emerald-600' : 'text-gray-400'}`} />
                          <Label className="text-xs cursor-pointer">Blend into document</Label>
                        </div>
                        <Checkbox
                          checked={selectedFieldData.blend_into_document || false}
                          onCheckedChange={(checked) => updateField(selectedField, { blend_into_document: checked })}
                          data-testid="blend-into-document-checkbox"
                        />
                      </div>
                    )}

                    {/* Write Back to Bullhorn — hidden when this field is
                        mapped to the virtual Candidate Reference target,
                        since reference fields are written via a separate
                        PUT /entity/CandidateReference flow, not a normal
                        field write-back. */}
                    {!(selectedFieldData.bullhorn_source_field === '__candidate_reference__'
                       || selectedFieldData.mapped_bullhorn_field === '__candidate_reference__') && (
                      <div 
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={() => updateField(selectedField, { write_back_to_bullhorn: !selectedFieldData.write_back_to_bullhorn })}
                      >
                        <div className="flex items-center gap-2">
                          <ArrowUpDown className={`w-3.5 h-3.5 ${selectedFieldData.write_back_to_bullhorn ? 'text-blue-600' : 'text-gray-400'}`} />
                          <Label className="text-xs cursor-pointer">Write back on completion</Label>
                        </div>
                        <Checkbox
                          checked={selectedFieldData.write_back_to_bullhorn || false}
                          onCheckedChange={(checked) => updateField(selectedField, { write_back_to_bullhorn: checked })}
                          data-testid="write-back-checkbox"
                        />
                      </div>
                    )}

                    {/* Date source — date fields only. Picks where the
                        value comes from at completion:
                          - manual          : candidate types it (default)
                          - completion_date : server-stamped at submit time
                          - sent_date       : server-stamped at SEND time
                                              (so the value lives in
                                              field_values from the moment
                                              the recruiter fires it)
                        Locked at the UI level when not "manual" so the
                        candidate can't tamper with the stamped value.
                        Back-compat: the legacy `auto_fill_completion_date`
                        boolean keeps working — when set we treat it as
                        `date_source = "completion_date"`. */}
                    {selectedFieldData.field_type === 'date' && (() => {
                      const currentSource = selectedFieldData.date_source
                        || (selectedFieldData.auto_fill_completion_date ? 'completion_date' : 'manual');
                      return (
                        <div className="space-y-1 py-2 px-3 rounded-lg bg-orange-50/60 border border-orange-100">
                          <div className="flex items-center gap-2">
                            <Calendar className={`w-3.5 h-3.5 ${currentSource === 'manual' ? 'text-gray-400' : 'text-orange-600'}`} />
                            <Label className="text-xs">Date source</Label>
                          </div>
                          <Select
                            value={currentSource}
                            onValueChange={(v) => {
                              const updates = { date_source: v };
                              // Drop the legacy boolean so we don't have two
                              // sources of truth that can drift apart.
                              updates.auto_fill_completion_date = (v === 'completion_date');
                              updateField(selectedField, updates);
                            }}
                          >
                            <SelectTrigger className="h-8 text-xs" data-testid="date-source-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="manual">Candidate selects (default)</SelectItem>
                              <SelectItem value="completion_date">Date of completion (today)</SelectItem>
                              <SelectItem value="sent_date">Date document was sent</SelectItem>
                            </SelectContent>
                          </Select>
                          {currentSource !== 'manual' && (
                            <p className="text-[11px] text-orange-700/80 leading-snug pt-1">
                              {currentSource === 'completion_date'
                                ? 'Stamped server-side when the candidate hits submit.'
                                : 'Stamped server-side when the recruiter sends the document.'}
                              {' '}Locked on the candidate&apos;s form.
                            </p>
                          )}
                        </div>
                      );
                    })()}

                    {/* Standalone-checkbox overlay toggle. Same idea as
                        the radio_group block above — when the recruiter
                        places a tick over an existing PDF checkbox, the
                        colored field box otherwise covers the printed
                        outline. Hide the chrome and the dot lands cleanly
                        inside the PDF box. */}
                    {selectedFieldData.field_type === 'checkbox' && (
                      <div
                        className="flex items-center justify-between py-2 px-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
                        onClick={() => updateField(selectedField, { transparent_background: !selectedFieldData.transparent_background })}
                      >
                        <div className="min-w-0 pr-2">
                          <Label className="text-xs cursor-pointer block">Transparent background</Label>
                          <p className="text-[10px] text-muted-foreground leading-snug">Drop the colored box so a tick lands inside the existing PDF checkbox.</p>
                        </div>
                        <Checkbox
                          checked={!!selectedFieldData.transparent_background}
                          onCheckedChange={(checked) => updateField(selectedField, { transparent_background: checked })}
                          data-testid="checkbox-transparent-background"
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Reference Group — only rendered when the recruiter has
                    explicitly picked the "Candidate Reference" mapping
                    from the Map to Field dropdown. Tagging a field as a
                    reference disables normal field write-back; instead
                    fields with the same reference_index are grouped and
                    POSTed to Bullhorn as a single CandidateReference. */}
                {entityType === 'candidate' && (
                  (selectedFieldData.bullhorn_source_field === '__candidate_reference__'
                   || selectedFieldData.mapped_bullhorn_field === '__candidate_reference__') && (
                    <div className="p-3 rounded-lg bg-purple-50/60 border border-purple-200" data-testid="reference-group-block">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Users className="w-3.5 h-3.5 text-purple-700" />
                        <Label className="text-xs font-medium text-purple-900">Candidate reference</Label>
                      </div>
                      <p className="text-xs text-purple-700/80 mb-2 leading-snug">
                        Fields with the same Reference number are saved as one Bullhorn reference on completion.
                      </p>
                      <div className="grid grid-cols-2 gap-2">
                        <Select
                          value={String(selectedFieldData.reference_index || 1)}
                          onValueChange={(v) => updateField(selectedField, {
                            reference_index: parseInt(v, 10),
                          })}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid="reference-index-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Reference 1</SelectItem>
                            <SelectItem value="2">Reference 2</SelectItem>
                            <SelectItem value="3">Reference 3</SelectItem>
                            <SelectItem value="4">Reference 4</SelectItem>
                            <SelectItem value="5">Reference 5</SelectItem>
                          </SelectContent>
                        </Select>
                        <Select
                          value={selectedFieldData.reference_role || 'name'}
                          onValueChange={(v) => updateField(selectedField, { reference_role: v })}
                        >
                          <SelectTrigger className="h-8 text-xs" data-testid="reference-role-select">
                            <SelectValue placeholder="Field role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="name">Full name</SelectItem>
                            <SelectItem value="first_name">First name</SelectItem>
                            <SelectItem value="last_name">Last name</SelectItem>
                            <SelectItem value="email">Email</SelectItem>
                            <SelectItem value="phone">Phone</SelectItem>
                            <SelectItem value="company_name">Company</SelectItem>
                            <SelectItem value="position">Position / Job title</SelectItem>
                            <SelectItem value="relationship">Relationship</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )
                )}

                {/* Conditional Logic Section */}
                <div className="pt-3 mt-2 border-t border-border">
                  <div className="flex items-center gap-2 mb-3">
                    <GitBranch className="w-4 h-4 text-purple-500" />
                    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Conditional Logic</span>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Enable/Disable Condition */}
                    <div 
                      className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => {
                        if (selectedFieldData.condition) {
                          updateField(selectedField, { condition: null });
                        } else {
                          updateField(selectedField, { 
                            condition: { field_id: '', operator: 'equals', value: '' } 
                          });
                        }
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${selectedFieldData.condition ? 'bg-purple-500' : 'bg-gray-300'}`} />
                        <Label className="text-xs cursor-pointer">Show only when...</Label>
                      </div>
                      <Checkbox
                        checked={!!selectedFieldData.condition}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            updateField(selectedField, { 
                              condition: { field_id: '', operator: 'equals', value: '' } 
                            });
                          } else {
                            updateField(selectedField, { condition: null });
                          }
                        }}
                      />
                    </div>

                    {/* Condition Configuration */}
                    {selectedFieldData.condition && (
                      <div className="space-y-2 p-3 bg-purple-50/50 rounded-lg border border-purple-100">
                        {/* Source Field */}
                        <div className="space-y-1">
                          <Label className="text-xs">When this field:</Label>
                          <select
                            value={selectedFieldData.condition.field_id || ''}
                            onChange={(e) => updateField(selectedField, {
                              condition: { ...selectedFieldData.condition, field_id: e.target.value }
                            })}
                            className="w-full h-8 px-2 text-sm rounded border border-input bg-background"
                          >
                            <option value="">Select a field...</option>
                            {fields
                              .filter(f => f.id !== selectedField && !['heading', 'paragraph', 'divider', 'page_break'].includes(f.field_type))
                              .map(f => (
                                <option key={f.id} value={f.id}>
                                  {f.label} ({FIELD_TYPES.find(t => t.value === f.field_type)?.label})
                                </option>
                              ))
                            }
                          </select>
                        </div>

                        {/* Operator */}
                        <div className="space-y-1">
                          <Label className="text-xs">Condition:</Label>
                          <select
                            value={selectedFieldData.condition.operator || 'equals'}
                            onChange={(e) => updateField(selectedField, {
                              condition: { ...selectedFieldData.condition, operator: e.target.value }
                            })}
                            className="w-full h-8 px-2 text-sm rounded border border-input bg-background"
                          >
                            <option value="equals">Equals</option>
                            <option value="not_equals">Does not equal</option>
                            <option value="contains">Contains</option>
                            <option value="is_checked">Is checked (for checkboxes)</option>
                            <option value="is_not_checked">Is not checked</option>
                            <option value="is_filled">Has any value</option>
                            <option value="is_empty">Is empty</option>
                          </select>
                        </div>

                        {/* Value (not needed for is_checked, is_not_checked, is_filled, is_empty) */}
                        {!['is_checked', 'is_not_checked', 'is_filled', 'is_empty'].includes(selectedFieldData.condition.operator) && (
                          <div className="space-y-1">
                            <Label className="text-xs">Value:</Label>
                            {(() => {
                              const sourceField = fields.find(f => f.id === selectedFieldData.condition.field_id);
                              if (sourceField && (sourceField.field_type === 'dropdown' || sourceField.field_type === 'radio_group')) {
                                return (
                                  <select
                                    value={selectedFieldData.condition.value || ''}
                                    onChange={(e) => updateField(selectedField, {
                                      condition: { ...selectedFieldData.condition, value: e.target.value }
                                    })}
                                    className="w-full h-8 px-2 text-sm rounded border border-input bg-background"
                                  >
                                    <option value="">Select value...</option>
                                    {(sourceField.options || []).map((opt, optIdx) => (
                                      <option key={`${optIdx}-${opt}`} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                );
                              }
                              return (
                                <Input
                                  value={selectedFieldData.condition.value || ''}
                                  onChange={(e) => updateField(selectedField, {
                                    condition: { ...selectedFieldData.condition, value: e.target.value }
                                  })}
                                  placeholder="Enter value to match"
                                  className="h-8 text-sm"
                                />
                              );
                            })()}
                          </div>
                        )}

                        <p className="text-xs text-purple-600/70 pt-1">
                          This field will be hidden until the condition is met.
                        </p>
                      </div>
                    )}

                    {/* Disable-when condition — parallel to "Show only when"
                        but instead of hiding the field, it disables it.
                        Use case: a "Yes / No" pair of checkboxes that should
                        function as mutually-exclusive: tick "Yes" → "No" is
                        locked out, and vice-versa. Schema is intentionally
                        identical to `field.condition` so the candidate-side
                        evaluator can reuse the same operator semantics. */}
                    <div
                      className="flex items-center justify-between py-2 px-3 rounded-lg bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => {
                        if (selectedFieldData.disable_when) {
                          updateField(selectedField, { disable_when: null });
                        } else {
                          updateField(selectedField, {
                            disable_when: { field_id: '', operator: 'equals', value: '' }
                          });
                        }
                      }}
                      data-testid="disable-when-toggle"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${selectedFieldData.disable_when ? 'bg-amber-500' : 'bg-gray-300'}`} />
                        <Label className="text-xs cursor-pointer">Disable when...</Label>
                      </div>
                      <Checkbox
                        checked={!!selectedFieldData.disable_when}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            updateField(selectedField, {
                              disable_when: { field_id: '', operator: 'equals', value: '' }
                            });
                          } else {
                            updateField(selectedField, { disable_when: null });
                          }
                        }}
                      />
                    </div>

                    {selectedFieldData.disable_when && (
                      <div className="space-y-2 p-3 bg-amber-50/50 rounded-lg border border-amber-100" data-testid="disable-when-config">
                        <div className="space-y-1">
                          <Label className="text-xs">When this field:</Label>
                          <select
                            value={selectedFieldData.disable_when.field_id || ''}
                            onChange={(e) => updateField(selectedField, {
                              disable_when: { ...selectedFieldData.disable_when, field_id: e.target.value }
                            })}
                            className="w-full h-8 px-2 text-sm rounded border border-input bg-background"
                          >
                            <option value="">Select a field...</option>
                            {fields
                              .filter(f => f.id !== selectedField && !['heading', 'paragraph', 'divider', 'page_break'].includes(f.field_type))
                              .map(f => (
                                <option key={f.id} value={f.id}>
                                  {f.label} ({FIELD_TYPES.find(t => t.value === f.field_type)?.label})
                                </option>
                              ))
                            }
                          </select>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Condition:</Label>
                          <select
                            value={selectedFieldData.disable_when.operator || 'equals'}
                            onChange={(e) => updateField(selectedField, {
                              disable_when: { ...selectedFieldData.disable_when, operator: e.target.value }
                            })}
                            className="w-full h-8 px-2 text-sm rounded border border-input bg-background"
                          >
                            <option value="equals">Equals</option>
                            <option value="not_equals">Does not equal</option>
                            <option value="contains">Contains</option>
                            <option value="is_checked">Is checked (for checkboxes)</option>
                            <option value="is_not_checked">Is not checked</option>
                            <option value="is_filled">Has any value</option>
                            <option value="is_empty">Is empty</option>
                          </select>
                        </div>

                        {!['is_checked', 'is_not_checked', 'is_filled', 'is_empty'].includes(selectedFieldData.disable_when.operator) && (
                          <div className="space-y-1">
                            <Label className="text-xs">Value:</Label>
                            {(() => {
                              const sourceField = fields.find(f => f.id === selectedFieldData.disable_when.field_id);
                              if (sourceField && (sourceField.field_type === 'dropdown' || sourceField.field_type === 'radio_group')) {
                                return (
                                  <select
                                    value={selectedFieldData.disable_when.value || ''}
                                    onChange={(e) => updateField(selectedField, {
                                      disable_when: { ...selectedFieldData.disable_when, value: e.target.value }
                                    })}
                                    className="w-full h-8 px-2 text-sm rounded border border-input bg-background"
                                  >
                                    <option value="">Select value...</option>
                                    {(sourceField.options || []).map((opt, optIdx) => (
                                      <option key={`${optIdx}-${opt}`} value={opt}>{opt}</option>
                                    ))}
                                  </select>
                                );
                              }
                              return (
                                <Input
                                  value={selectedFieldData.disable_when.value || ''}
                                  onChange={(e) => updateField(selectedField, {
                                    disable_when: { ...selectedFieldData.disable_when, value: e.target.value }
                                  })}
                                  placeholder="Enter value to match"
                                  className="h-8 text-sm"
                                />
                              );
                            })()}
                          </div>
                        )}

                        <p className="text-xs text-amber-700/80 pt-1">
                          This field will be locked (read-only) when the condition is met. Tip: add the mirror rule on the other field to make them act as a "tick one or the other" pair.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Keyboard hint */}
                <p className="text-xs text-center text-muted-foreground pt-2">
                  Press <kbd className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">Del</kbd> to delete • <kbd className="px-1.5 py-0.5 rounded bg-muted text-[10px] font-mono">Ctrl+Z</kbd> to undo
                </p>
              </div>
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                <Move className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-sm font-medium">No field selected</p>
                <p className="text-xs mt-2">
                  Click a field to edit, or add new fields from the left panel
                </p>
                {pdfInfo.has_pdf && fields.length === 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4 bg-gradient-to-r from-slate-50 to-orange-50 border-orange-200"
                    onClick={() => handleAIDetection(true)}
                    disabled={detecting}
                  >
                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                      <path d="M2 17l10 5 10-5"/>
                      <path d="M2 12l10 5 10-5"/>
                    </svg>
                    Auto-detect with AI
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* AI Detection Preview Modal */}
      {showDetectedPreview && detectedFields && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="ai-detection-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-scale-in">
            {/* Modal Header */}
            <div className="p-6 border-b border-border bg-gradient-to-r from-slate-50 to-orange-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#f97316] to-[#f59e0b] flex items-center justify-center shadow-md">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                    <path d="M2 17l10 5 10-5"/>
                    <path d="M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold">AI Detected Fields</h2>
                  <p className="text-sm text-muted-foreground">
                    Found {detectedFields.length} fields in your document
                  </p>
                </div>
              </div>
            </div>

            {/* Fields List */}
            <div className="p-6 overflow-auto max-h-[400px]">
              <div className="space-y-3">
                {detectedFields.map((field, index) => (
                  <div 
                    key={field.id}
                    className={`p-3 rounded-lg border ${FIELD_COLORS[field.field_type]} flex items-center justify-between`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-white/80 flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{field.label}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="capitalize">{field.field_type}</span>
                          {field.required && <span className="text-red-500">• Required</span>}
                          {field.mapped_bullhorn_field && (
                            <span className="text-forest">• {field.mapped_bullhorn_field}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-border bg-muted/30">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {fields.length > 0 
                    ? `You have ${fields.length} existing fields. Choose how to apply.`
                    : 'Apply these fields to your template.'}
                </p>
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    onClick={cancelDetectedFields}
                    data-testid="cancel-detection"
                  >
                    Cancel
                  </Button>
                  {fields.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={() => applyDetectedFields('merge')}
                      data-testid="merge-fields"
                    >
                      Add to Existing
                    </Button>
                  )}
                  <Button
                    className="btn-primary"
                    onClick={() => applyDetectedFields('replace')}
                    data-testid="replace-fields"
                  >
                    {fields.length > 0 ? 'Replace All' : 'Apply Fields'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Test Field Mapping Dialog */}
      <Dialog open={testMappingOpen} onOpenChange={setTestMappingOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="test-mapping-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Beaker className="w-5 h-5 text-forest" />
              Test Field Mapping
            </DialogTitle>
            <DialogDescription>
              Pick a Bullhorn {entityType} and see the exact values that would pre-fill from Bullhorn for every mapped field in this template.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-2">
            {/* Search for candidate/contact/placement by name */}
            <div>
              <Label htmlFor="test-search-query">Search {entityType}s by name or email</Label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="test-search-query"
                    value={testSearchQuery}
                    onChange={(e) => setTestSearchQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleTestSearch(); } }}
                    placeholder={entityType === 'candidate'
                      ? 'Search candidates by name or email'
                      : entityType === 'contact'
                      ? 'Search contacts by name or email'
                      : 'Search placements by candidate, job title or company'}
                    className="pl-9"
                    data-testid="test-mapping-search"
                  />
                </div>
                <Button
                  variant="outline"
                  onClick={handleTestSearch}
                  disabled={testSearching || testSearchQuery.trim().length < 2}
                  data-testid="test-mapping-search-button"
                >
                  {testSearching ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Search'}
                </Button>
              </div>
              
              {/* Search results dropdown */}
              {testSearchResults.length > 0 && (
                <div className="mt-2 max-h-48 overflow-auto rounded-lg border bg-white shadow-sm" data-testid="test-mapping-search-results">
                  {testSearchResults.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => selectTestResult(r)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b last:border-b-0 flex items-center justify-between gap-2"
                      data-testid={`test-mapping-result-${r.id}`}
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">{r.name}</div>
                        {r.subtitle && (
                          <div className="text-xs text-muted-foreground truncate">{r.subtitle}</div>
                        )}
                      </div>
                      <span className="text-xs font-mono text-muted-foreground flex-shrink-0">#{r.id}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Manual ID fallback / selected display */}
            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label htmlFor="test-bullhorn-id">
                  {testSelectedName ? (
                    <span>Selected: <span className="text-forest font-medium">{testSelectedName}</span></span>
                  ) : (
                    `Or enter Bullhorn ${entityType} ID directly`
                  )}
                </Label>
                <Input
                  id="test-bullhorn-id"
                  value={testBullhornId}
                  onChange={(e) => { setTestBullhornId(e.target.value); setTestSelectedName(''); }}
                  placeholder="Paste Bullhorn ID"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleTestMapping(); }}
                  data-testid="test-mapping-bullhorn-id"
                />
              </div>
              <Button
                onClick={handleTestMapping}
                disabled={testMappingLoading || !testBullhornId.trim()}
                className="btn-primary"
                data-testid="run-test-mapping"
              >
                {testMappingLoading ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Fetching…</>
                ) : (
                  <><Beaker className="w-4 h-4 mr-2" />Run Test</>
                )}
              </Button>
            </div>
          </div>

          {testMappingError && (
            <div className="mt-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="test-mapping-error">
              {testMappingError}
            </div>
          )}

          {testMappingResult && (
            <div className="mt-4 flex-1 overflow-auto" data-testid="test-mapping-results">
              {testMappingResult.total_mapped === 0 ? (
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                  No fields in this template are mapped to Bullhorn yet. Add a "Map to Field" selection on your fields first.
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-4 mb-3">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1.5 text-emerald-700">
                        <CheckCircle2 className="w-4 h-4" />
                        <strong>{testMappingResult.filled}</strong> filled
                      </span>
                      <span className="flex items-center gap-1.5 text-slate-500">
                        <XCircle className="w-4 h-4" />
                        <strong>{testMappingResult.empty}</strong> empty
                      </span>
                      <span className="text-muted-foreground">
                        / {testMappingResult.total_mapped} mapped
                      </span>
                    </div>
                    {/* Opens a transient DocumentInstance pre-filled from the
                        same Bullhorn record the recruiter just searched.
                        Shows exactly what the candidate would see — overlay
                        boxes for editable fields, blend styling for locked,
                        currency formatting, conditional rules. Submit is
                        blocked on the candidate page in preview mode. */}
                    <Button
                      onClick={async () => {
                        if (!testBullhornId?.trim()) {
                          toast.error('No Bullhorn record selected');
                          return;
                        }
                        const tid = templateId;
                        toast.loading('Building preview…', { id: 'preview-build' });
                        try {
                          const res = await axios.post(`${API_URL}/templates/${tid}/candidate-preview`, {
                            bullhorn_id: String(testBullhornId).trim(),
                            entity_type: entityType || 'candidate',
                          });
                          toast.success('Preview ready', { id: 'preview-build' });
                          window.open(res.data.preview_url, '_blank', 'noopener,noreferrer');
                        } catch (err) {
                          toast.error(
                            err?.response?.data?.detail || 'Failed to build preview',
                            { id: 'preview-build' }
                          );
                        }
                      }}
                      className="bg-orange-600 hover:bg-orange-700 text-white"
                      data-testid="open-candidate-preview"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview as {entityType === 'contact' ? 'Contact' : entityType === 'placement' ? 'Placement Recipient' : 'Candidate'}
                    </Button>
                  </div>
                  <div className="rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 border-b">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-slate-600">Field</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-600">Bullhorn Source</th>
                          <th className="text-left px-3 py-2 font-medium text-slate-600">Live Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {testMappingResult.results.map((r) => (
                          <tr key={r.field_id} className="border-b last:border-b-0 hover:bg-slate-50">
                            <td className="px-3 py-2">
                              <div className="font-medium text-slate-800">{r.label}</div>
                              <div className="text-xs text-muted-foreground">{r.field_type}</div>
                            </td>
                            <td className="px-3 py-2 font-mono text-xs text-slate-600">
                              {r.bullhorn_field}
                            </td>
                            <td className="px-3 py-2">
                              {r.has_value ? (
                                <span className="inline-flex items-center gap-1.5 text-emerald-700">
                                  <CheckCircle2 className="w-3.5 h-3.5 flex-shrink-0" />
                                  {/* #14: show the resolved name (e.g. skill name), not the raw Bullhorn id */}
                                  <span className="break-all">{r.display_value ?? r.value}</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 text-slate-400 italic">
                                  <XCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                  empty in Bullhorn
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Settings dialog: per-template instructions, attachment-only mode */}
      <Dialog open={showSettingsPanel} onOpenChange={setShowSettingsPanel}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-orange-500" />
              Document Settings
            </DialogTitle>
            <DialogDescription>
              {(() => {
                // Adapt the recipient wording to the template's entity type
                // so contact templates don't refer to "candidate".
                const who =
                  entityType === 'contact' ? 'client contact'
                    : entityType === 'placement' ? 'recipient'
                    : 'candidate';
                return `Personalise what the ${who} sees when they open this template.`;
              })()}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Informational-only toggle — sits at the top of the panel
                because flipping it changes the whole shape of the doc
                (no required gating, no write-back). */}
            <div className="flex items-start justify-between gap-4 p-3 rounded-lg border border-slate-200 bg-slate-50">
              <div className="flex-1 min-w-0">
                <Label htmlFor="informational-toggle" className="text-sm font-medium cursor-pointer">
                  Informational only
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Read-only on the candidate side. No required fields, no Bullhorn write-back —
                  use for Welcome Packs, Privacy Notices, "for your records" sends.
                </p>
              </div>
              <Switch
                id="informational-toggle"
                checked={isInformational}
                onCheckedChange={setIsInformational}
                data-testid="informational-toggle"
              />
            </div>

            {/* Mode selector */}
            <div className="space-y-2">
              <Label>Template type</Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setTemplateMode('form')}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${templateMode === 'form' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid="template-mode-form"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className="w-4 h-4 text-orange-600" />
                    <span className="font-medium text-sm">Form template</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {entityType === 'contact'
                      ? 'Client contact fills in fields on a PDF.'
                      : entityType === 'placement'
                      ? 'Recipient fills in fields on a PDF.'
                      : 'Candidate fills in fields on a PDF.'}
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateMode('attachment_only')}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${templateMode === 'attachment_only' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid="template-mode-attachment-only"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Paperclip className="w-4 h-4 text-orange-600" />
                    <span className="font-medium text-sm">Attachment only</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {entityType === 'contact'
                      ? 'Client contact uploads files (e.g. signed agreement, ID). No PDF to fill.'
                      : entityType === 'placement'
                      ? 'Recipient uploads files (e.g. ID documents). No PDF to fill.'
                      : 'Candidate uploads files (e.g. passport, driving licence). No PDF to fill.'}
                  </p>
                </button>
              </div>
            </div>

            {/* Delivery — for non-placement templates (single party).
                Placement templates use the per-party section below. */}
            {templateMode === 'form' && entityType !== 'placement' && (
              <div className="space-y-2" data-testid="delivery-mode-section">
                <Label>Delivery</Label>
                <p className="text-xs text-muted-foreground">
                  Default behaviour when this template is sent. Recruiters can override per send.
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setDeliveryMode('interactive')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${deliveryMode === 'interactive' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
                    data-testid="delivery-mode-interactive"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-4 h-4 text-orange-600" />
                      <span className="font-medium text-sm">Interactive link</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Recipient opens a secure link, fills in fields, and signs in the browser.
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeliveryMode('pdf_attachment')}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${deliveryMode === 'pdf_attachment' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'}`}
                    data-testid="delivery-mode-pdf-attachment"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Paperclip className="w-4 h-4 text-orange-600" />
                      <span className="font-medium text-sm">PDF attachment</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Recipient receives the PDF in their inbox. Nothing to open, no fields to fill. Auto-uploaded to Bullhorn Files.
                    </p>
                  </button>
                </div>
              </div>
            )}

            {/* Placement-only — per-party routing. A placement template
                can spawn 0/1/2 documents on send depending on which
                parties are enabled. Each enabled party gets its own
                delivery mode. */}
            {templateMode === 'form' && entityType === 'placement' && (
              <div className="space-y-3" data-testid="placement-routing-section">
                <div>
                  <Label>Placement routing</Label>
                  <p className="text-xs text-muted-foreground">
                    Choose which parties on the placement should receive this document — and how.
                    A single send can spawn up to two documents, one per enabled party.
                  </p>
                </div>

                {!sendToCandidate && !sendToContact && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    At least one party must be enabled — otherwise the template won't send to anyone.
                  </p>
                )}

                {/* Candidate party */}
                <div className={`rounded-lg border-2 transition-all ${sendToCandidate ? 'border-emerald-300 bg-emerald-50/40' : 'border-slate-200 bg-slate-50/40'}`}>
                  <label className="flex items-start gap-3 p-3 cursor-pointer" data-testid="placement-send-to-candidate">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
                      checked={sendToCandidate}
                      onChange={(e) => setSendToCandidate(e.target.checked)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">Send to candidate</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">CANDIDATE</span>
                      </div>
                      <p className="text-xs text-muted-foreground">The candidate associated with this placement.</p>
                    </div>
                  </label>
                  {sendToCandidate && (
                    <div className="px-3 pb-3 pt-1 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setCandidateDeliveryMode('interactive')}
                        className={`p-2.5 rounded-lg border-2 text-left transition-all ${candidateDeliveryMode === 'interactive' ? 'border-emerald-500 bg-white' : 'border-slate-200 bg-white hover:border-emerald-300'}`}
                        data-testid="candidate-delivery-mode-interactive"
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <FileText className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="font-medium text-xs">Interactive link</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground">Candidate fills in fields and signs.</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setCandidateDeliveryMode('pdf_attachment')}
                        className={`p-2.5 rounded-lg border-2 text-left transition-all ${candidateDeliveryMode === 'pdf_attachment' ? 'border-emerald-500 bg-white' : 'border-slate-200 bg-white hover:border-emerald-300'}`}
                        data-testid="candidate-delivery-mode-pdf"
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Paperclip className="w-3.5 h-3.5 text-emerald-600" />
                          <span className="font-medium text-xs">PDF attachment</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground">Candidate gets a pre-filled PDF copy.</p>
                      </button>
                    </div>
                  )}
                </div>

                {/* Contact party */}
                <div className={`rounded-lg border-2 transition-all ${sendToContact ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200 bg-slate-50/40'}`}>
                  <label className="flex items-start gap-3 p-3 cursor-pointer" data-testid="placement-send-to-contact">
                    <input
                      type="checkbox"
                      className="mt-0.5 w-4 h-4 rounded text-amber-600 focus:ring-amber-500"
                      checked={sendToContact}
                      onChange={(e) => setSendToContact(e.target.checked)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">Send to client contact</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">CONTACT</span>
                      </div>
                      <p className="text-xs text-muted-foreground">The client contact associated with this placement.</p>
                    </div>
                  </label>
                  {sendToContact && (
                    <div className="px-3 pb-3 pt-1 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setContactDeliveryMode('interactive')}
                        className={`p-2.5 rounded-lg border-2 text-left transition-all ${contactDeliveryMode === 'interactive' ? 'border-amber-500 bg-white' : 'border-slate-200 bg-white hover:border-amber-300'}`}
                        data-testid="contact-delivery-mode-interactive"
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <FileText className="w-3.5 h-3.5 text-amber-600" />
                          <span className="font-medium text-xs">Interactive link</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground">Client contact fills in fields and signs.</p>
                      </button>
                      <button
                        type="button"
                        onClick={() => setContactDeliveryMode('pdf_attachment')}
                        className={`p-2.5 rounded-lg border-2 text-left transition-all ${contactDeliveryMode === 'pdf_attachment' ? 'border-amber-500 bg-white' : 'border-slate-200 bg-white hover:border-amber-300'}`}
                        data-testid="contact-delivery-mode-pdf"
                      >
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <Paperclip className="w-3.5 h-3.5 text-amber-600" />
                          <span className="font-medium text-xs">PDF attachment</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground">Client contact gets a pre-filled PDF copy.</p>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Bullhorn Credentials mapping (candidate & placement templates only). */}
            {templateMode === 'form' && entityType !== 'contact' && (
              <CredentialMappingSection
                enabled={credentialMappingEnabled}
                setEnabled={setCredentialMappingEnabled}
                credentialId={credentialId}
                credentialName={credentialName}
                onChange={(id, name) => {
                  setCredentialId(id);
                  setCredentialName(name);
                }}
                fields={fields}
                onToggleExpiryField={(fieldId, isExpiry) => {
                  setFields((prev) =>
                    prev.map((f) =>
                      f.id === fieldId
                        ? { ...f, is_credential_expiry: isExpiry }
                        // Only one field can be the expiry — unset others.
                        : (isExpiry ? { ...f, is_credential_expiry: false } : f)
                    )
                  );
                }}
                entityType={entityType}
              />
            )}

            {/* Description — the same field set (optionally) when the template was
                created; editing it here updates that too. Shown to whoever
                completes the document, at the top, above the fields. */}
            <div className="space-y-2">
              <Label>Description</Label>
              <p className="text-xs text-muted-foreground">
                {entityType === 'contact'
                  ? 'Shown above the document when the client contact opens it to complete. Supports basic formatting and links.'
                  : entityType === 'placement'
                  ? 'Shown above the document when the recipient opens it to complete. Supports basic formatting and links.'
                  : 'Shown above the document when the candidate opens it to complete. Supports basic formatting and links.'}
              </p>
              <RichTextEditor
                testId="candidate-instructions-editor"
                value={candidateInstructions}
                onChange={setCandidateInstructions}
                placeholder={entityType === 'contact'
                  ? 'e.g. Please review and sign the agreement below. If you have any questions, reach out to your account manager.'
                  : 'e.g. Please attach a copy of your passport and proof of address. Both must be in colour and clearly readable.'}
                rows={6}
              />
            </div>

            {/* Completion notifications — per-document override of the
                workspace-wide defaults set in Settings → Notifications.
                Left on "Use workspace default" (null) for every existing
                template until explicitly changed here. */}
            <div className="space-y-3 border-t pt-5" data-testid="completion-notifications-section">
              <div>
                <Label className="flex items-center gap-1.5">
                  <Mail className="w-4 h-4 text-orange-500" />
                  Completion notifications
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Override the workspace's default completion emails for this document specifically.
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">Notify sender when completed</Label>
                  <Select
                    value={notifySenderOnCompletion == null ? 'inherit' : (notifySenderOnCompletion ? 'true' : 'false')}
                    onValueChange={(v) => setNotifySenderOnCompletion(v === 'inherit' ? null : v === 'true')}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="notify-sender-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">Use workspace default</SelectItem>
                      <SelectItem value="true">Always notify</SelectItem>
                      <SelectItem value="false">Never notify</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">
                    Notify {entityType === 'contact' ? 'client contact' : entityType === 'placement' ? 'recipient' : 'candidate'} when completed
                  </Label>
                  <Select
                    value={notifyRecipientOnCompletion == null ? 'inherit' : (notifyRecipientOnCompletion ? 'true' : 'false')}
                    onValueChange={(v) => setNotifyRecipientOnCompletion(v === 'inherit' ? null : v === 'true')}
                  >
                    <SelectTrigger className="h-9 text-sm" data-testid="notify-recipient-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="inherit">Use workspace default</SelectItem>
                      <SelectItem value="true">Always notify</SelectItem>
                      <SelectItem value="false">Never notify</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Whether the recipient's confirmation email above also
                  carries a copy of the completed document itself — opt-in,
                  aimed at client-facing contracts (a HyreDocs SaaS
                  Agreement, an SOW) rather than every recruitment
                  onboarding document. Note: if this document has any
                  signature field, the certificate is already permanently
                  part of that file (appended once, at completion, so it
                  also flows back to Bullhorn's Files tab under Flow back —
                  that's unconditional and unaffected by this setting); this
                  only controls whether a copy is additionally emailed.
                  Has no effect if the recipient notification itself is set
                  to "Never notify". */}
              <div
                className="flex items-start gap-3 p-3 rounded-lg border-2 border-border hover:border-primary/30 cursor-pointer transition-all"
                onClick={() => setSendCompletedCopyToRecipient(!sendCompletedCopyToRecipient)}
                data-testid="send-completed-copy-toggle"
              >
                <Checkbox
                  checked={sendCompletedCopyToRecipient}
                  onCheckedChange={(checked) => setSendCompletedCopyToRecipient(!!checked)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex-1">
                  <p className="text-sm font-medium">
                    Email the {entityType === 'contact' ? 'client contact' : entityType === 'placement' ? 'recipient' : 'candidate'} a copy once completed
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Sends them the finished document (with its Certificate of Completion, if signed) once every party has completed their part.
                  </p>
                </div>
              </div>

              {/* Additional recipients (CC) override — same field as
                  Settings → Notifications → Additional recipients, but
                  scoped to this template's own completion emails. null =
                  inherit the workspace list. */}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium text-muted-foreground">Additional recipients (CC)</Label>
                <Select
                  value={notificationCcEmails == null ? 'inherit' : 'custom'}
                  onValueChange={(v) => {
                    if (v === 'inherit') { setNotificationCcEmails(null); setNotificationCcInput(''); }
                    else setNotificationCcEmails(notificationCcEmails ?? []);
                  }}
                >
                  <SelectTrigger className="h-9 text-sm" data-testid="notification-cc-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">Use workspace default</SelectItem>
                    <SelectItem value="custom">Custom recipients</SelectItem>
                  </SelectContent>
                </Select>
                {notificationCcEmails != null && (
                  <>
                    <Textarea
                      value={notificationCcInput}
                      onChange={(e) => setNotificationCcInput(e.target.value)}
                      placeholder="hr@yourcompany.com, compliance@yourcompany.com"
                      rows={2}
                      className="text-sm"
                      data-testid="notification-cc-input"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Comma-, semicolon-, or newline-separated. Leave blank to CC no one for this template. Replaces the workspace list entirely — doesn't add to it.
                    </p>
                  </>
                )}
              </div>
            </div>

            {/* Automated reminder cadence — per-document override of the
                workspace-wide default (Settings → Notifications → Default
                reminder cadence). Left on "Use workspace default" (null)
                for every existing template until explicitly changed here. */}
            <div className="space-y-3 border-t pt-5" data-testid="reminder-cadence-section">
              <div>
                <Label className="flex items-center gap-1.5">
                  <Bell className="w-4 h-4 text-orange-500" />
                  Reminder cadence
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  How often automated reminders go out for this document specifically, overriding the workspace default.
                </p>
              </div>
              <div className="flex items-center gap-3 max-w-sm">
                <Select
                  value={reminderDays == null ? 'inherit' : 'custom'}
                  onValueChange={(v) => setReminderDays(v === 'inherit' ? null : (reminderDays ?? 3))}
                >
                  <SelectTrigger className="h-9 text-sm flex-1" data-testid="reminder-cadence-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">Use workspace default</SelectItem>
                    <SelectItem value="custom">Custom cadence</SelectItem>
                  </SelectContent>
                </Select>
                {reminderDays != null && (
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="1"
                      max="90"
                      value={reminderDays}
                      onChange={(e) => setReminderDays(Math.max(1, Math.min(90, parseInt(e.target.value, 10) || 1)))}
                      className="h-9 w-20 text-sm"
                      data-testid="reminder-cadence-days-input"
                    />
                    <span className="text-sm text-muted-foreground whitespace-nowrap">days</span>
                  </div>
                )}
              </div>
            </div>

            {/* Estimated completion time shown to candidates. A measured median
                (from real completions) always wins once the template has
                enough of them; this manual figure only fills the gap before
                that. Blank = show nothing rather than a guess. */}
            <div className="space-y-3 border-t pt-5" data-testid="estimated-time-section">
              <div>
                <Label htmlFor="estimated-minutes-input" className="flex items-center gap-1.5">
                  <Timer className="w-4 h-4 text-orange-500" />
                  Estimated completion time
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Shown to candidates as &quot;About N minutes&quot; until this document has enough completions for HyreDocs to measure the real median, after which the measured time is used instead. Leave blank to show nothing until then.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <Input
                  id="estimated-minutes-input"
                  type="number"
                  min="1"
                  max="480"
                  step="1"
                  inputMode="numeric"
                  placeholder="e.g. 5"
                  value={estimatedMinutes}
                  onChange={(e) => {
                    const raw = e.target.value;
                    setEstimatedMinutes(raw === '' ? '' : String(Math.max(1, Math.min(480, parseInt(raw, 10) || 1))));
                  }}
                  className="h-9 w-24 text-sm"
                  data-testid="estimated-minutes-input"
                />
                <span className="text-sm text-muted-foreground whitespace-nowrap">minutes</span>
              </div>
            </div>

            {/* Attachment checklist (attachment_only mode) */}
            {templateMode === 'attachment_only' && (
              <div className="space-y-2 border-t pt-5" data-testid="attachment-checklist-section">
                <div className="flex items-center justify-between">
                  <Label>Required attachments</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setAttachmentChecklist((prev) => [
                        ...prev,
                        { id: `att-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: '', required: true },
                      ])
                    }
                    data-testid="add-checklist-item-button"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add attachment
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Define which files the candidate must upload. Mark each as required or optional.
                </p>
                {attachmentChecklist.length === 0 ? (
                  <div className="text-center py-6 border-2 border-dashed border-slate-200 rounded-lg text-sm text-slate-500">
                    No attachments defined yet — click <strong>Add attachment</strong> to begin.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {attachmentChecklist.map((item, idx) => (
                      <div
                        key={item.id}
                        className="flex items-center gap-2 p-2 border border-slate-200 rounded-lg bg-white"
                        data-testid={`checklist-item-${idx}`}
                      >
                        <Paperclip className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <Input
                          value={item.name}
                          onChange={(e) =>
                            setAttachmentChecklist((prev) =>
                              prev.map((c, ci) => (ci === idx ? { ...c, name: e.target.value } : c))
                            )
                          }
                          placeholder="e.g. Passport, Driving licence, Proof of address"
                          className="flex-1 h-9 text-sm"
                          data-testid={`checklist-item-name-${idx}`}
                        />
                        <label className="flex items-center gap-1.5 text-xs text-slate-700 select-none cursor-pointer">
                          <Switch
                            checked={item.required}
                            onCheckedChange={(checked) =>
                              setAttachmentChecklist((prev) =>
                                prev.map((c, ci) => (ci === idx ? { ...c, required: checked } : c))
                              )
                            }
                            data-testid={`checklist-item-required-${idx}`}
                          />
                          {item.required ? 'Required' : 'Optional'}
                        </label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setAttachmentChecklist((prev) => prev.filter((_, ci) => ci !== idx))
                          }
                          className="h-9 w-9 p-0 text-slate-400 hover:text-red-500"
                          data-testid={`remove-checklist-item-${idx}`}
                        >
                          <XIcon className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-amber-600 flex items-start gap-1.5 mt-2">
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  Candidates still sign an acknowledgement and an audit certificate is generated — same legal-grade flow as form templates.
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-4 border-t">
            <Button variant="ghost" onClick={() => setShowSettingsPanel(false)} data-testid="cancel-template-settings">
              Cancel
            </Button>
            <Button
              onClick={async () => {
                await handleSave();
                setShowSettingsPanel(false);
              }}
              disabled={saving}
              className="btn-primary"
              data-testid="save-template-settings"
            >
              {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save settings
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
